"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  createElement,
} from "react";
// DWDM figures in the strings below read from the data files, never literals.
import {
  DWDM_CAPACITY_RANGE_LABEL,
  IP_TRANSIT_MIN_LABEL,
  IP_TRANSIT_MAX_LABEL,
  DWDM_CAPACITY_MAX_LABEL,
  DWDM_CAPACITY_STEPS_LABEL,
} from "@/lib/pricing";
import {
  DWDM_SLA_PERCENT,
  IP_TRANSIT_SLA_PERCENT,
  COUNTRIES_SERVED_COUNT,
} from "@/lib/network-metrics";
import { popsFor } from "@/data/service-availability";
import { leadTimeFor, formatLeadTimeDays } from "@/data/provisioning-lead-times";

const DWDM_POP_COUNT = popsFor("dwdm").length;
const DWDM_LEAD_EN = formatLeadTimeDays(leadTimeFor("dwdm"), "en");
const DWDM_LEAD_BG = formatLeadTimeDays(leadTimeFor("dwdm"), "bg");

// ─── Types ────────────────────────────────────────────────────────────────────

export type Language = "en" | "bg";

type Translations = {
  // Header & Navigation
  services: string;
  network: string;
  tools: string;
  get_a_quote: string;
  naas_platform: string;
  more: string;

  // Hero
  hero_title_1: string;
  hero_title_2: string;
  hero_subtitle: string;
  explore_network: string;
  try_naas_configurator: string;

  // Trust metrics
  pops: string;
  to_frankfurt: string;
  uptime_sla: string;
  bgp_peers: string;
  // Signal rebrand: extracted from WhySection hardcoded labels so BG users
  // stop seeing untranslated English "Points of Presence" / "Countries".
  // Not new copy — pulled straight from the network's public facts.
  points_of_presence: string;
  countries_kpi: string;
  // Signal rebrand: spec-row labels for schematic service cards. Mono,
  // lowercase, terminal-typography aesthetic. Not new copy — established
  // technical vocabulary already used in Tech Specs tables.
  spec_capacity: string;
  spec_ports: string;
  spec_sla: string;
  spec_lead: string;
  spec_fibre: string;
  spec_scope: string;
  spec_scope_sofia_metro: string;
  spec_days_short: string;

  // Footer section headers
  products: string;
  resources: string;
  legal: string;
  all_rights_reserved: string;

  // Common
  contact: string;
  contact_us: string;
  learn_more: string;

  // WhySection
  why_section_eyebrow: string;
  why_section_heading: string;

  // WhySection differentiator titles
  diff_strategic_location: string;
  diff_ai_ready: string;
  diff_naas: string;
  diff_noc: string;

  // BentoServices
  bento_eyebrow: string;
  bento_heading: string;
  bento_subtitle: string;

  // BentoServices - service descriptions
  ip_transit_desc: string;
  l2vpn_desc: string;
  dwdm_desc: string;
  dark_fiber_desc: string;

  // NaaS Configurator
  naas_eyebrow: string;
  naas_heading: string;
  naas_subtitle: string;

  // GlobalLiveMesh
  mesh_eyebrow: string;
  mesh_heading: string;
  mesh_desc: string;
  mesh_view_all_pops: string;

  // TrustedBy
  trusted_eyebrow: string;
  trusted_heading: string;
  trusted_subtitle: string;

  // Testimonials
  testimonials_eyebrow: string;
  testimonials_heading: string;
  testimonials_subtitle: string;

  read_faq: string;

  // FAQ
  faq_eyebrow: string;
  faq_heading: string;
  faq_subtitle: string;
  still_have_questions: string;
  faq_team_responds: string;
  contact_sales: string;

  // CTA Banner
  cta_heading: string;
  cta_subtitle: string;
  try_naas: string;

  // WhySection differentiator descriptions
  diff_strategic_desc: string;
  diff_ai_desc: string;
  diff_naas_desc: string;
  diff_noc_desc: string;

  // WhySection fact box
  why_fact_text: string;
  why_fact_as: string;
  experience_diff: string;

  // Footer link labels
  footer_peering: string;
  footer_network_status: string;
  footer_looking_glass: string;
  footer_naas_configurator: string;
  footer_developer_portal: string;
  footer_client_portal: string;
  footer_latency_checker: string;
  footer_roi_calculator: string;
  footer_network_advisor: string;
  footer_ai_advisor: string;
  footer_provisioning_demo: string;
  footer_knowledge_base: string;
  footer_news: string;
  footer_insights: string;
  footer_faq: string;
  footer_terms: string;
  footer_privacy: string;
  footer_aup: string;
  footer_sla: string;

  // Personalized subtitle
  welcome_back: string;

  // Dropdown descriptions (Header)
  desc_ip_transit: string;
  desc_l2vpn: string;
  desc_dwdm: string;
  desc_dark_fiber: string;
  desc_ai_ready: string;
  desc_solution_finder: string;
  desc_peering: string;
  desc_network_status: string;
  desc_looking_glass: string;
  desc_network_mesh: string;
  desc_developer_portal: string;
  desc_latency_checker: string;
  desc_roi_calculator: string;
  desc_network_advisor: string;
  desc_ai_advisor: string;
  desc_provisioning: string;
  desc_bandwidth_calculator: string;

  // —— /tools/bandwidth-calculator/ ——
  bw_nav_label: string;
  bw_page_title: string;
  bw_page_description: string;
  bw_h1: string;
  bw_eyebrow: string;
  bw_intro: string;
  bw_indicative_line: string;

  bw_primer_heading: string;
  bw_primer_1_title: string;
  bw_primer_1_body: string;
  bw_primer_2_title: string;
  bw_primer_2_body: string;
  bw_primer_3_title: string;
  bw_primer_3_body: string;

  bw_step_inputs: string;
  bw_step_results: string;

  bw_arch_label: string;
  bw_arch_cap: string;
  bw_arch_mix: string;
  bw_arch_inf: string;
  bw_arch_cap_desc: string;
  bw_arch_mix_desc: string;
  bw_arch_inf_desc: string;

  bw_mw_label: string;
  bw_mw_unit: string;
  bw_mw_hint: string;
  bw_P_label: string;
  bw_P_unit: string;
  bw_P_hint: string;
  bw_drain_label: string;
  bw_drain_unit: string;
  bw_drain_hint: string;
  bw_interval_label: string;
  bw_interval_unit: string;
  bw_interval_hint: string;
  bw_bursters_label: string;
  bw_bursters_hint: string;
  bw_bursters_readout: string; // e.g. "{count} (phi {phi})" — mono font lacks the Greek block, so spell it out here

  bw_card_avg_title: string;
  bw_card_avg_sub: string; // "{value} Gb/s per MW"
  bw_card_peak_title: string;
  bw_card_peak_sub: string;
  bw_card_link_title: string;
  bw_card_link_sub: string;
  bw_card_lambdas_title: string;
  bw_card_lambdas_sub: string;
  bw_card_lambdas_value: string; // "{n} ×400G"

  bw_breakdown_title: string;
  bw_breakdown_sub: string;
  bw_flow_checkpoint: string;
  bw_flow_dataset: string;
  bw_flow_inference: string;
  bw_flow_distill: string;
  bw_flow_ops: string;
  bw_flow_row_aria: string; // "{flow}: {value} at peak, {share}% of total"

  bw_copy_button: string;
  bw_copy_done: string;
  bw_share_button: string;
  bw_share_copied: string;
  bw_reset_button: string;

  bw_notes_heading: string;
  bw_note_mw_title: string;
  bw_note_mw_body: string;
  bw_note_phi_title: string;
  bw_note_phi_body: string;
  bw_note_limits_title: string;
  bw_note_limits_body: string;

  bw_cta_heading: string;
  bw_cta_body: string;
  bw_cta_primary: string;
  bw_cta_secondary: string;
  bw_cta_note: string;

  bw_copy_summary_title: string; // header line in copied text
  bw_copy_summary_methodology: string; // methodology line in copied text

  // ── Contact page ──
  contact_eyebrow: string;
  contact_hero_title: string;
  contact_hero_subtitle: string;
  contact_channel_sales: string;
  contact_channel_noc: string;
  contact_channel_phone: string;
  contact_channel_office: string;
  contact_channel_live: string;
  contact_office_details: string;
  contact_office_legal_name: string;
  contact_office_asn: string;
  contact_office_hours: string;
  contact_office_hours_value: string;
  contact_office_noc_availability: string;

  // ── Contact form ──
  contact_form_title: string;
  contact_form_subtitle: string;
  contact_form_name: string;
  contact_form_name_placeholder: string;
  contact_form_company: string;
  contact_form_company_placeholder: string;
  contact_form_email: string;
  contact_form_email_placeholder: string;
  contact_form_phone: string;
  contact_form_phone_placeholder: string;
  contact_form_department: string;
  contact_form_department_placeholder: string;
  contact_form_service: string;
  contact_form_service_placeholder: string;
  contact_form_message: string;
  contact_form_message_placeholder: string;
  contact_form_submit: string;
  contact_form_submitting: string;
  contact_form_success_title: string;
  contact_form_success_text: string;
  contact_form_send_another: string;
  contact_form_error_title: string;
  contact_form_error_text_prefix: string;
  contact_form_error_text_or: string;
  contact_form_try_again: string;

  // ── Status badge ──
  status_all_operational: string;
  status_degraded: string;
  status_outage: string;
  status_loading: string;
  status_unknown: string;
  status_short_operational: string;
  status_short_degraded: string;
  status_short_outage: string;
  status_short_loading: string;
  status_short_unknown: string;
  status_aria_prefix: string;

  // ── /status/ demonstration page ──
  status_demo_eyebrow: string;
  status_demo_h1: string;
  status_demo_intro: string;
  status_demo_banner_title: string;
  status_demo_banner_body_1: string;
  status_demo_banner_body_2: string;
  status_demo_banner_body_or: string;
  status_demo_banner_aria: string;
  status_demo_pill_operational: string;
  status_demo_pill_degraded: string;
  status_demo_pill_outage: string;
  status_demo_pill_aria: string;
  status_demo_sla_headline_eyebrow: string;
  status_demo_sla_headline_note: string;
  status_demo_routes_h2: string;
  status_demo_routes_subnote: string;
  status_demo_routes_refresh_note: string;
  status_demo_services_h2: string;
  status_demo_services_subnote: string;
  status_demo_services_footnote: string;
  status_demo_col_service: string;
  status_demo_col_sla_target: string;
  status_demo_col_uptime_target: string;
  status_demo_col_max_downtime: string;
  status_demo_col_response: string;
  status_demo_col_repair: string;
  status_demo_sla_h2: string;
  status_demo_sla_subnote: string;
  status_demo_sla_footnote: string;
  status_demo_latency_h2: string;
  status_demo_latency_subnote: string;
  status_demo_noc_h2: string;
  status_demo_noc_body: string;
  status_demo_next_refresh: string;

  // /looking-glass/ demonstration page
  lg_demo_eyebrow: string;
  lg_demo_h1: string;
  lg_demo_intro: string;
  lg_demo_banner_title: string;
  lg_demo_banner_body_1: string;
  lg_demo_banner_body_2: string;
  lg_demo_banner_body_or: string;
  lg_demo_banner_aria: string;
  lg_demo_result_notice: string;
  lg_demo_footer_note: string;

  // ── NaaS configurator step labels ──
  naas_step_product: string;
  naas_step_route: string;
  naas_step_capacity: string;
  naas_step_terms: string;
  naas_step_review: string;

  // ── Resources page ──
  resources_eyebrow: string;
  resources_hero_title: string;
  resources_hero_subtitle: string;
  resources_category_all: string;
  resources_category_case_studies: string;
  resources_category_documentation: string;
  resources_category_network_updates: string;
  resources_show_less: string;
  resources_read_more: string;
  resources_view_document: string;
  resources_no_resources: string;

  // ── FAQ Q&A (12 items) ──
  faq_q_ip_transit: string;
  faq_a_ip_transit: string;
  faq_q_latency_frankfurt: string;
  faq_a_latency_frankfurt: string;
  faq_q_latency_istanbul: string;
  faq_a_latency_istanbul: string;
  faq_q_latency_london: string;
  faq_a_latency_london: string;
  faq_q_dark_fiber: string;
  faq_a_dark_fiber: string;
  faq_q_dwdm: string;
  faq_a_dwdm: string;
  faq_q_pops: string;
  faq_a_pops: string;
  faq_q_naas: string;
  faq_a_naas: string;
  faq_q_sla: string;
  faq_a_sla: string;
  faq_q_quote: string;
  faq_a_quote: string;
  faq_q_asn: string;
  faq_a_asn: string;
  faq_q_ipv6: string;
  faq_a_ipv6: string;
  faq_aria_list: string;

  // ── Consent banner + preference centre (Task 1) ──
  consent_banner_title: string;
  consent_banner_body: string;
  consent_banner_accept: string;
  consent_banner_reject: string;
  consent_banner_preferences: string;
  consent_banner_learn_more: string;
  consent_banner_aria_label: string;

  consent_prefs_title: string;
  consent_prefs_intro: string;
  consent_prefs_essential_title: string;
  consent_prefs_essential_body: string;
  consent_prefs_essential_state: string;
  consent_prefs_analytics_title: string;
  consent_prefs_analytics_body: string;
  consent_prefs_analytics_state_granted: string;
  consent_prefs_analytics_state_denied: string;
  consent_prefs_toggle_aria: string;
  consent_prefs_save: string;
  consent_prefs_reject_all: string;
  consent_prefs_accept_all: string;
  consent_prefs_close: string;
  consent_prefs_status_saved: string;

  footer_cookie_preferences: string;

  // ── Header + Footer navigation labels (PR #116) ──
  nav_about: string;
  nav_about_desc: string;
  nav_locations: string;
  nav_locations_desc_short: string;
  nav_locations_desc_full: string;
  nav_case_studies: string;
  nav_case_studies_desc: string;
  nav_news_short: string;
  nav_news_desc: string;
  nav_knowledge_base_short: string;
  nav_knowledge_base_desc: string;
  nav_resources_desc: string;
  nav_interactive_map_3d: string;
  nav_interactive_map_3d_desc: string;
  footer_web_design: string;
  footer_powered_by_vercel: string;
  footer_vercel_tooltip: string;

  // ── SC-NIA transparency notice (Task 2) ──
  ai_notice_heading: string;
  ai_notice_body: string;
  ai_notice_privacy_link: string;
  ai_notice_dismiss: string;

  // ── Form Art. 13 notices (Task 3) ──
  form_notice_contact: string;
  form_notice_newsletter: string;
  form_notice_configurator: string;
  form_notice_privacy_link: string;

  // ── SC-NIA chat drawer (F4 fix) ──
  scnia_open_aria: string;
  scnia_close_aria: string;
  scnia_send_aria: string;
  scnia_agent_name: string;
  scnia_context_prefix: string;
  scnia_greeting: string;
  scnia_prompt: string;
  scnia_input_placeholder: string;
  scnia_input_placeholder_limit: string;
  scnia_beta_footer: string;
  scnia_message_limit: string;
  scnia_new_conversation: string;
  scnia_suggestion_services: string;
  scnia_suggestion_ip_transit: string;
  scnia_suggestion_l2vpn: string;
  scnia_suggestion_10g_quote: string;
  scnia_suggestion_services_desc: string;
  scnia_suggestion_ip_transit_desc: string;
  scnia_suggestion_l2vpn_desc: string;
  scnia_suggestion_10g_quote_desc: string;

  // ── SC-NIA full page (F4 fix) ──
  scnia_page_subtitle: string;
  scnia_page_choose_topic: string;
  scnia_page_quick_links: string;
  scnia_page_services_desc: string;
  scnia_page_network_desc: string;
  scnia_page_configurator_label: string;
  scnia_page_configurator_desc: string;
  scnia_page_contact_desc: string;
  scnia_page_beta_mode: string;
  scnia_page_beta_disclaimer_lead: string;
  scnia_page_beta_disclaimer_middle: string;
  scnia_page_beta_disclaimer_or: string;
  scnia_page_beta_disclaimer_end: string;
  scnia_page_configurator_link_label: string;
  scnia_page_contact_link_label: string;
  scnia_page_direct_contact: string;
  scnia_page_sales_department: string;

  // ── /network/ scrollytelling page ──
  network_page_eyebrow: string;
  network_page_title: string;
  network_page_intro: string;
  network_page_cta_configure: string;
  network_page_cta_talk_engineering: string;
  network_page_toggle_show_edge: string;
  network_page_toggle_hide_edge: string;
  // Phase 1 — Gateway
  network_page_p1_eyebrow: string;
  network_page_p1_title: string;
  network_page_p1_body: string;
  network_page_p1_cta1: string;
  network_page_p1_cta2: string;
  // Phase 2 — Hub
  network_page_p2_eyebrow: string;
  network_page_p2_title: string;
  network_page_p2_body: string;
  network_page_p2_cta1: string;
  network_page_p2_cta2: string;
  // Phase 3 — Bridge
  network_page_p3_eyebrow: string;
  network_page_p3_title: string;
  network_page_p3_body: string;
  network_page_p3_cta1: string;
  network_page_p3_cta2: string;
  // Phase 4 — Corridor
  network_page_p4_eyebrow: string;
  network_page_p4_title: string;
  network_page_p4_body: string;
  network_page_p4_cta1: string;
  network_page_p4_cta2: string;
  // Phase 5 — Onward
  network_page_p5_eyebrow: string;
  network_page_p5_title: string;
  network_page_p5_body: string;
  network_page_p5_cta1: string;
  network_page_p5_cta2: string;
  // Explore mode
  network_page_explore_eyebrow: string;
  network_page_explore_title: string;
  network_page_explore_body: string;
  network_page_explore_cta1: string;
  network_page_explore_cta2: string;
  // Bottom 2D map section
  network_page_map_eyebrow: string;
  network_page_map_title: string;
  network_page_map_aria_label: string;

  // ── Mobile mesh / InteractiveNetworkMap (mobile homepage + /network/ bottom section) ──
  // Loader while the Leaflet map bundle is fetching.
  imap_loading: string;
  // Live-metrics strip on MobileNetworkMesh (HEALTH / AVG RTT / POPS LIVE).
  mesh_metric_health: string;
  mesh_metric_avg_rtt: string;
  mesh_metric_pops_live: string;
  // Balchik CLS highlight card copy — the Caucasus Cable System differentiator.
  mesh_balchik_body_prefix: string;
  mesh_balchik_body_bypass: string;
  mesh_balchik_body_suffix: string;
  mesh_balchik_sofia_to_balchik_label: string;
  // Collapsible PoP-group titles.
  mesh_group_international: string;
  mesh_group_sc_edge_bg: string;
  // Footer strap — uses {count} and {asn} placeholders.
  mesh_footer_note: string;
  // Route Tracer controls inside InteractiveNetworkMap.
  imap_route_tracer: string;
  imap_from_placeholder: string;
  imap_to_placeholder: string;
  imap_reset: string;
  imap_route_result_label: string;
  imap_est_latency_label: string;
  // Legend items.
  imap_legend_primary: string;
  imap_legend_caucasus: string;
  imap_legend_submarine: string;
  imap_legend_bg_edge: string;
  // ── /solutions/ai-ready-infrastructure/ (AI interconnect page) ──
  ai_page_eyebrow: string;
  ai_page_h1: string;
  ai_page_subhead: string;
  ai_page_scope: string;
  ai_page_cta_talk: string;
  ai_page_cta_network: string;
  ai_page_l1_eyebrow: string;
  ai_page_l1_heading: string;
  ai_page_l1_body: string;
  ai_page_spec_dedicated: string;
  ai_page_spec_deterministic: string;
  ai_page_spec_coherent: string;
  ai_page_spec_protection: string;
  ai_page_power_eyebrow: string;
  ai_page_power_heading: string;
  ai_page_power_body: string;
  ai_page_services_eyebrow: string;
  ai_page_services_heading: string;
  ai_page_svc_dwdm_body: string;
  ai_page_svc_ip_body: string;
  ai_page_svc_l2_body: string;
  ai_page_svc_df_body: string;
  ai_page_svc_delivery: string;
  ai_page_svc_lead: string;
  ai_page_svc_sla: string;
  ai_page_svc_pops: string;
  ai_page_learn_more: string;
  ai_page_corridor_eyebrow: string;
  ai_page_corridor_heading: string;
  ai_page_corridor_reach: string;
  ai_page_lat_frankfurt: string;
  ai_page_lat_istanbul: string;
  ai_page_ccs_body: string;
  ai_page_ix_heading: string;
  ai_page_ix_port: string;
  ai_page_calc_cta: string;
  ai_page_calc_note: string;
  ai_page_final_heading: string;
  ai_page_final_body: string;
  ai_page_final_quote: string;
  ai_page_final_services: string;
};

// ─── Translations dictionary ───────────────────────────────────────────────────

const translations: Record<Language, Translations> = {
  en: {
    // Header & Navigation
    services: "Services",
    network: "Network",
    tools: "Tools",
    get_a_quote: "Get a Quote",
    naas_platform: "NaaS Platform",
    more: "More",

    // Hero
    hero_title_1: "The Carrier Corridor Between",
    hero_title_2: "Middle East, Caucasus and Europe",
    hero_subtitle:
      "Two-way fiber, DWDM and IP transit linking Dubai, Yerevan, Tbilisi and Istanbul with Frankfurt, Sofia, Athens and London \u2014 one AS, one SLA, one NOC.",
    explore_network: "Explore Network",
    try_naas_configurator: "Try NaaS Configurator",

    // Trust metrics
    pops: "PoPs",
    to_frankfurt: "to Frankfurt",
    uptime_sla: "Uptime SLA",
    bgp_peers: "BGP Peers",
    points_of_presence: "Points of Presence",
    countries_kpi: "Countries",
    spec_capacity: "capacity",
    spec_ports: "ports",
    spec_sla: "sla",
    spec_lead: "lead",
    spec_fibre: "fibre",
    spec_scope: "scope",
    spec_scope_sofia_metro: "Sofia metro",
    spec_days_short: "d",

    // Footer section headers
    products: "Products",
    resources: "Resources",
    legal: "Legal",
    all_rights_reserved: "All rights reserved.",

    // Common
    contact: "Contact",
    contact_us: "Contact Us",
    learn_more: "Learn More",

    // WhySection
    why_section_eyebrow: "The Difference",
    why_section_heading: "Why Sofia Connect",

    // WhySection differentiator titles
    diff_strategic_location: "Strategic Location",
    diff_ai_ready: "AI-Ready Capacity",
    diff_naas: "NaaS Self-Service",
    diff_noc: "24/7 NOC",

    // BentoServices
    bento_eyebrow: "Connectivity Products",
    bento_heading: "Built for Scale",
    bento_subtitle:
      "From raw fiber to fully managed cloud on-ramps — everything your network needs, available on-demand.",

    // BentoServices - service descriptions
    ip_transit_desc:
      `Premium global routing via Tier-1 peering across multiple PoPs. ${IP_TRANSIT_MIN_LABEL} to ${IP_TRANSIT_MAX_LABEL}, flexible billing.`,
    l2vpn_desc:
      "Transparent Layer 2 connectivity. Point-to-point and multipoint over MPLS backbone.",
    dwdm_desc:
      `Dedicated optical capacity, ${DWDM_CAPACITY_RANGE_LABEL} per wavelength. Coherent optics, deterministic latency.`,
    dark_fiber_desc:
      "Unlit G.652D fibre in the Sofia metro today; national and cross-border corridors on the roadmap. Your equipment, your protocols.",

    // NaaS Configurator
    naas_eyebrow: "Solution Architect",
    naas_heading: "Design Your Connectivity",
    naas_subtitle:
      "Get real-time indicative pricing and send a quote request directly — no forms to fill manually.",

    // GlobalLiveMesh
    mesh_eyebrow: "Real-Time Network Mesh",
    mesh_heading: "Global Live Mesh",
    mesh_desc: "Interactive 3D view of our infrastructure with live traffic flows. Click any route to inspect Jitter and Packet Loss. Click a node for details.",
    mesh_view_all_pops: "View all 18 PoPs →",

    // TrustedBy
    trusted_eyebrow: "Trusted Worldwide",
    trusted_heading: "Network Ecosystem",
    trusted_subtitle:
      "Peers, upstreams, and customers across Europe, the Caucasus, and the Middle East that interconnect with Sofia Connect.",

    // Testimonials
    testimonials_eyebrow: "Partner References",
    testimonials_heading: "What our carrier partners say",
    testimonials_subtitle:
      "Verified references from international carriers we serve across the Middle East, Caucasus and Europe corridor.",

    read_faq: "Read the FAQ",

    // FAQ
    faq_eyebrow: "Carrier Knowledge Base",
    faq_heading: "Frequently Asked Questions",
    faq_subtitle:
      "Technical and commercial answers for network engineers, procurement teams, and carrier relations — covering services, SLAs, and how to get started.",
    still_have_questions: "Still have questions?",
    faq_team_responds:
      "Our carrier relations team responds within one business day.",
    contact_sales: "Contact Sales",

    // CTA Banner
    cta_heading: "Ready to Connect?",
    cta_subtitle:
      "Whether you need a quote, a consultation, or support — our network engineers are available around the clock.",
    try_naas: "Try the NaaS Configurator",

    // WhySection differentiator descriptions
    diff_strategic_desc:
      "Positioned at the crossroads of Europe, the Balkans, and Turkey — routes that most carriers would need 3+ hops to reach.",
    diff_ai_desc:
      `Dedicated wavelengths up to ${DWDM_CAPACITY_MAX_LABEL} with deterministic latency between AI sites, exchanges and cloud on-ramps.`,
    diff_naas_desc:
      "Configure, order, and manage connectivity on-demand through our portal or API. Real-time pricing, instant provisioning.",
    diff_noc_desc:
      "Round-the-clock network operations with financial SLA penalties, not marketing promises.",

    // WhySection fact box
    why_fact_text:
      "Sofia Connect operates 18 PoPs — 9 international (Sofia, Frankfurt, Istanbul, London, Tbilisi, Athens, Dubai, Yerevan, Agarak) and 9 Sofia Connect Edge facilities across Bulgaria, including the Balchik Caucasus Cable System landing station that bypasses Türkiye terrestrial transit.",
    why_fact_as: "Autonomous System: AS47872 · Registered with RIPE NCC",
    experience_diff: "Experience the Difference",

    // Footer link labels
    footer_peering: "Peering & IX (AS47872)",
    footer_network_status: "Network Status (demo)",
    footer_looking_glass: "Looking Glass (demo)",
    footer_naas_configurator: "NaaS Configurator",
    footer_developer_portal: "Developer Portal",
    footer_client_portal: "Client Portal",
    footer_latency_checker: "Latency Checker",
    footer_roi_calculator: "ROI Calculator",
    footer_network_advisor: "Network Advisor",
    footer_ai_advisor: "SC-NIA",
    footer_provisioning_demo: "Provisioning Demo",
    footer_knowledge_base: "Knowledge Base",
    footer_news: "News & Updates",
    footer_insights: "Insights",
    footer_faq: "FAQ",
    footer_terms: "Terms of Service",
    footer_privacy: "Privacy Policy",
    footer_aup: "Acceptable Use Policy",
    footer_sla: "SLA Summary",

    // Personalized subtitle
    welcome_back: "Welcome back — pick up where you left off",

    // Dropdown descriptions (Header)
    desc_ip_transit: "High-capacity global IP routing",
    desc_l2vpn: "Private Layer 2 connectivity",
    desc_dwdm: `${DWDM_CAPACITY_RANGE_LABEL} optical transport`,
    desc_dark_fiber: "Unlit fiber for full control",
    desc_ai_ready: "GPU-optimised low-latency fabric",
    desc_solution_finder: "Guided quiz to find your service",
    desc_peering: "Exchange points & route servers",
    desc_network_status: "Status page demonstration",
    desc_looking_glass: "BGP & traceroute demo",
    desc_network_mesh: "3D live topology view",
    desc_developer_portal: "API docs & integrations",
    desc_latency_checker: "One-click latency test",
    desc_roi_calculator: "Savings & ROI estimator",
    desc_network_advisor: "Personalized connectivity plan",
    desc_ai_advisor: "Network Intelligence Agent — technical consultation, API, monitoring",
    desc_provisioning: "Interactive order walkthrough",
    desc_bandwidth_calculator: "North-south bandwidth sizing per MW",

    // —— /tools/bandwidth-calculator/ (en) ——
    bw_nav_label: "Bandwidth Calculator",
    bw_page_title: "Bandwidth Calculator",
    bw_page_description:
      "Size north-south WAN capacity for an AI data centre by installed megawatts, model size and checkpoint policy. Indicative model — not a measurement, not a capacity commitment.",
    bw_h1: "AI Data Centre Bandwidth Calculator",
    bw_eyebrow: "NORTH-SOUTH WAN SIZING",
    bw_intro:
      "North-south traffic at an AI site is a function of installed IT load, the size of the largest model, and how often checkpoints drain. Everything below is normalised per megawatt. Five independent flows are modelled; average and peak are dominated by different flows.",
    bw_indicative_line:
      "Indicative model · published assumptions, not a measurement or a capacity commitment. Route-specific design available on request.",

    bw_primer_heading: "Three things worth knowing before you slide",
    bw_primer_1_title: "Megawatts, not GPUs.",
    bw_primer_1_body:
      "Traffic doesn’t scale with GPU count — it scales with megawatts. Card generations turn over every eighteen months and their count per watt shifts each generation; traffic per watt barely moves. Sizing by GPU dates fast.",
    bw_primer_2_title: "Peak and average are driven by different things.",
    bw_primer_2_body:
      "Average is inference. Peak is almost entirely checkpoint drains. Mix them and you’re wrong in both directions at once.",
    bw_primer_3_title: "The Φ coefficient.",
    bw_primer_3_body:
      "Independent tenants don’t checkpoint on the same second, so their peaks don’t add up. Three tenants → Φ ≈ 0.70; eight tenants → Φ ≈ 0.40. The practical takeaway: contractually staggering checkpoint windows is the cheapest way to buy yourself capacity.",

    bw_step_inputs: "Step 1 — Inputs",
    bw_step_results: "Step 2 — Results",

    bw_arch_label: "Site archetype",
    bw_arch_cap: "Captive training",
    bw_arch_mix: "Mixed neocloud",
    bw_arch_inf: "Inference-first",
    bw_arch_cap_desc:
      "A single tenant, mostly training. High checkpoint retention (R = 14), low inference intensity.",
    bw_arch_mix_desc:
      "Multiple tenants, mixed training and serving. R = 14, inference intensity 1.0.",
    bw_arch_inf_desc:
      "Predominantly serving. R = 2 (weights only), inference intensity 4.0. Peak dominated by inference bursts.",

    bw_mw_label: "Installed IT load",
    bw_mw_unit: "MW",
    bw_mw_hint: "Total IT power at the site, not the utility feed.",
    bw_P_label: "Largest model on site",
    bw_P_unit: "T params",
    bw_P_hint:
      "Trillions of parameters. Drives checkpoint volume: bytes per parameter × retention factor.",
    bw_drain_label: "Checkpoint drain window",
    bw_drain_unit: "s",
    bw_drain_hint:
      "Seconds to drain one checkpoint off-site. Shorter drains raise peak; longer drains average out.",
    bw_interval_label: "Checkpoint interval",
    bw_interval_unit: "min",
    bw_interval_hint:
      "Minutes between checkpoint events per burster. Sets the duty cycle for average bandwidth.",
    bw_bursters_label: "Independent bursters",
    bw_bursters_hint:
      "Concurrent training runs. Independent means their drain windows are uncorrelated — that is what lets Φ come down from 1.",
    bw_bursters_readout: "{count} (phi {phi})",

    bw_card_avg_title: "Average",
    bw_card_avg_sub: "{value} Gb/s per MW",
    bw_card_peak_title: "Peak after phi",
    bw_card_peak_sub: "{value} Gb/s per MW",
    bw_card_link_title: "Required link at 65% utilisation",
    bw_card_link_sub: "Peak divided by target utilisation.",
    bw_card_lambdas_title: "Lambdas across two diverse routes",
    bw_card_lambdas_sub: "Rounded up to an even count, half on each route.",
    bw_card_lambdas_value: "{n} ×400G",

    bw_breakdown_title: "Peak flow breakdown",
    bw_breakdown_sub: "Share of peak Tb/s per flow.",
    bw_flow_checkpoint: "Checkpoint drain",
    bw_flow_dataset: "Dataset ingest",
    bw_flow_inference: "Inference I/O",
    bw_flow_distill: "Distill · copy-out",
    bw_flow_ops: "Ops · control plane",
    bw_flow_row_aria: "{flow}: {value} at peak, {share}% of total.",

    bw_copy_button: "Copy summary",
    bw_copy_done: "Copied",
    bw_share_button: "Copy shareable link",
    bw_share_copied: "Link copied",
    bw_reset_button: "Reset",

    bw_notes_heading: "Methodology notes",
    bw_note_mw_title: "Why megawatts and not GPUs",
    bw_note_mw_body:
      "GPU generations change count per watt roughly every 18 months. Hopper to Blackwell to whatever comes next moves the FLOPs and the memory per watt, but north-south traffic per watt barely moves — checkpoints, dataset ingest and inference all scale with power delivered to compute, not with the accelerator SKU. Sizing on GPU count ages badly the moment a new generation lands on the floor.",
    bw_note_phi_title: "Why Φ exists",
    bw_note_phi_body:
      "Independent tenants' checkpoint drain windows are uncorrelated, so their peaks do not sum — the site sees the convolution, not the addition. Φ is the effective correlation factor across N bursters, defaulted here to 1.00 at N=1, 0.75 at N=2, and dropping 0.05 per additional burster to a floor of 0.4. Contractually staggering drain windows in the tenant SLA is the cheapest capacity upgrade available: with three or more bursters it drives Φ toward 0.4, i.e. more than halves peak checkpoint bandwidth without touching physical infrastructure.",
    bw_note_limits_title: "Model limits",
    bw_note_limits_body:
      "The model assumes independent bursters. A shared cluster scheduler correlates them and Φ returns to 1. Cross-site distributed training is not included — it is a separate 10–100 Tb/s term for parallelism between data centres and belongs in a route-level design conversation, not a per-site sizing. Inference coefficients are calibrated for text plus light retrieval-augmented generation; media generation (image, and especially video) breaks the scale by an order of magnitude and should be sized directly against measured egress from the serving stack.",

    bw_cta_heading: "We sell the lambdas this calculator counts.",
    bw_cta_body:
      "For a route-specific design — diverse paths, cross-connects, latency and delivery terms — open the configurator or talk to the network team. Both take the same brief.",
    bw_cta_primary: "Route-design enquiry",
    bw_cta_secondary: "Open the configurator",
    bw_cta_note:
      "The configurator is not pre-filled from this page — the model output is indicative and the configurator quotes real capacity. Bring the number over deliberately.",

    bw_copy_summary_title: "Sofia Connect — AI DC bandwidth model (indicative)",
    bw_copy_summary_methodology:
      "Indicative model based on published assumptions — not a Sofia Connect measurement, not a capacity commitment. Route-specific design available on request.",

    // Contact page
    contact_eyebrow: "Get in Touch",
    contact_hero_title: "Contact Us",
    contact_hero_subtitle:
      "Reach the right team directly — whether you're exploring new connectivity solutions or need immediate technical assistance.",
    contact_channel_sales: "Sales & Quotes",
    contact_channel_noc: "24/7 NOC",
    contact_channel_phone: "Phone",
    contact_channel_office: "Office",
    contact_channel_live: "Live",
    contact_office_details: "Office Details",
    contact_office_legal_name: "Registered Name",
    contact_office_asn: "ASN",
    contact_office_hours: "Office Hours",
    contact_office_hours_value: "Mon\u2013Fri, 09:00\u201318:00 EET",
    contact_office_noc_availability: "NOC Availability",

    // Contact form
    contact_form_title: "Send a Message",
    contact_form_subtitle: "Fill out the form and we'll get back to you within one business day.",
    contact_form_name: "Full Name",
    contact_form_name_placeholder: "John Smith",
    contact_form_company: "Company",
    contact_form_company_placeholder: "Acme Telecom",
    contact_form_email: "Email",
    contact_form_email_placeholder: "john@acmetelecom.com",
    contact_form_phone: "Phone (optional)",
    contact_form_phone_placeholder: "+359 2 123 4567",
    contact_form_department: "Department",
    contact_form_department_placeholder: "Select department",
    contact_form_service: "Service Interest",
    contact_form_service_placeholder: "Select service (optional)",
    contact_form_message: "Message",
    contact_form_message_placeholder: "Describe your connectivity requirements, volumes, timelines...",
    contact_form_submit: "Send Message",
    contact_form_submitting: "Sending...",
    contact_form_success_title: "Message Sent",
    contact_form_success_text:
      "Thank you for contacting Sofia Connect. Our team will respond within one business day. For urgent NOC issues, call us directly.",
    contact_form_send_another: "Send Another Message",
    contact_form_error_title: "Sending Failed",
    contact_form_error_text_prefix: "Something went wrong. Please try again or contact us directly at",
    contact_form_error_text_or: "or",
    contact_form_try_again: "Try Again",

    // Status badge
    status_all_operational: "All systems operational",
    status_degraded: "Degraded performance",
    status_outage: "Active incident",
    status_loading: "Checking status\u2026",
    status_unknown: "Status",
    status_short_operational: "Operational",
    status_short_degraded: "Degraded",
    status_short_outage: "Incident",
    status_short_loading: "\u2026",
    status_short_unknown: "Status",
    status_aria_prefix: "Network status",

    // /status/ demonstration page
    status_demo_eyebrow: "Demonstration — not live network data",
    status_demo_h1: "Network Status — Demonstration",
    status_demo_intro:
      "This page demonstrates what the Sofia Connect network status surface looks like across our {count} Points of Presence. All figures on this page are either contractual SLA targets or values generated in a sandbox — they do not reflect actual network performance.",
    status_demo_banner_title:
      "This is a demonstration page. Figures on this page are simulated.",
    status_demo_banner_body_1:
      "Route latency, jitter, packet loss and load are generated in a test environment, on the live site as well. They do not reflect actual network performance. Uptime figures are contractual SLA targets, not measurements.",
    status_demo_banner_body_2:
      "For real-time network status and incident reports, contact our NOC at",
    status_demo_banner_body_or: "or",
    status_demo_banner_aria: "Demonstration notice",
    status_demo_pill_operational: "Demo state — Operational",
    status_demo_pill_degraded: "Demo state — Degraded",
    status_demo_pill_outage: "Demo state — Outage",
    status_demo_pill_aria:
      "Demonstration state, computed from simulated metrics",
    status_demo_sla_headline_eyebrow: "IP Transit — contractual SLA target",
    status_demo_sla_headline_note:
      "Monthly availability commitment for IP Transit. Not a measurement.",
    status_demo_routes_h2: "Simulated route health",
    status_demo_routes_subnote:
      "Values below are generated in a sandbox. They do not reflect actual backbone performance.",
    status_demo_routes_refresh_note: "Simulated data — refreshes every 30s",
    status_demo_services_h2: "Contractual service SLA targets",
    status_demo_services_subnote:
      "Uptime figures below are contractual SLA commitments read from src/lib/network-metrics.ts. They are not measurements.",
    status_demo_services_footnote:
      "Response and repair times are contractual engagement targets, not observed values.",
    status_demo_col_service: "Service",
    status_demo_col_sla_target: "Uptime SLA target",
    status_demo_col_uptime_target: "Uptime SLA target",
    status_demo_col_max_downtime: "Max downtime",
    status_demo_col_response: "Response",
    status_demo_col_repair: "Repair",
    status_demo_sla_h2: "SLA commitments",
    status_demo_sla_subnote:
      "Contractual commitments per service class. Not a measurement of live performance.",
    status_demo_sla_footnote:
      "The Dark Fibre SLA covers the in-service Sofia metro dark fibre only. Planned routes to Kalotina and to the Greek border are excluded until their in-service dates.",
    status_demo_latency_h2: "Reference latency figures",
    status_demo_latency_subnote:
      "Reference values from our production DWDM corridor. Contractual/spec figures, not a live measurement.",
    status_demo_noc_h2: "Contact the NOC for real network status",
    status_demo_noc_body:
      "The Sofia Connect NOC is available 24/7 for real-time network status, incident reports and service-affecting events.",
    status_demo_next_refresh: "Next simulated refresh in {secs}s",

    // /looking-glass/ demonstration page (en)
    lg_demo_eyebrow: "Demonstration — not a live diagnostic tool",
    lg_demo_h1: "Looking Glass — Demonstration",
    lg_demo_intro:
      "This page demonstrates the look and feel of a network diagnostic surface for Sofia Connect. The form below does not execute BGP lookups, traceroutes or pings against our infrastructure — it renders a canned response. For real diagnostics from our network, contact the NOC.",
    lg_demo_banner_title:
      "This is a demonstration page. No query executed here reaches our network.",
    lg_demo_banner_body_1:
      "BGP route lookups, traceroutes and pings on this page return a canned response after a short delay. They do not query real routers, real routing tables or real destinations from any Sofia Connect Point of Presence.",
    lg_demo_banner_body_2:
      "For live BGP lookups, traceroutes and diagnostics from our network, contact our NOC at",
    lg_demo_banner_body_or: "or",
    lg_demo_banner_aria: "Demonstration notice",
    lg_demo_result_notice:
      "Demonstration result — no query was executed against the Sofia Connect network. For live diagnostics, contact the NOC.",
    lg_demo_footer_note:
      "For public looking-glass services, see RIPE RIS, RouteViews or PeeringDB.",

    // NaaS configurator step labels
    naas_step_product: "Product",
    naas_step_route: "Route",
    naas_step_capacity: "Capacity",
    naas_step_terms: "Terms",
    naas_step_review: "Review & Send",

    // Resources page
    resources_eyebrow: "Knowledge Hub",
    resources_hero_title: "Resources & Insights",
    resources_hero_subtitle:
      "Case studies, technical documentation, and network updates from Sofia Connect \u2014 carrier-grade connectivity provider in Southeast Europe.",
    resources_category_all: "All",
    resources_category_case_studies: "Case Studies",
    resources_category_documentation: "Documentation",
    resources_category_network_updates: "Network Updates",
    resources_show_less: "Show less",
    resources_read_more: "Read more",
    resources_view_document: "View document",
    resources_no_resources: "No resources in this category yet.",

    // FAQ Q&A
    faq_q_ip_transit: "What is IP Transit and how does Sofia Connect deliver it?",
    faq_a_ip_transit:
      "IP Transit is a paid service that grants access to the global internet routing table via a carrier's upstream network. Sofia Connect delivers IP Transit under AS47872, peering with 96+ networks at major European and regional Internet Exchange Points. Customers connect in Sofia (Equinix SO1/SO2, Telepoint) or Frankfurt (Equinix FR5, New Telco) and receive full-table BGP routing with redundant upstream paths, 24/7 NOC monitoring, and a 99.99% SLA backed by financial penalties. Port speeds range from 1G to 400G.",
    faq_q_latency_frankfurt: "What is the latency between Sofia and Frankfurt?",
    faq_a_latency_frankfurt:
      "The one-way latency between Sofia and Frankfurt on Sofia Connect's network is approximately 24.5 ms. This is achieved via a direct, low-hop-count fiber path between Sofia PoPs (Equinix SO1/SO2) and Frankfurt (Equinix FR5, New Telco), without unnecessary transit through third-party intermediaries. The route supports DWDM wavelengths up to 400G with coherent optics for deterministic, consistent latency profiles.",
    faq_q_latency_istanbul: "What is the latency between Sofia and Istanbul?",
    faq_a_latency_istanbul:
      "The one-way latency between Sofia and Istanbul on Sofia Connect's network is approximately 12 ms. Sofia's geographic proximity to Istanbul makes this one of the most competitive SE-Europe-to-Turkey paths available from a single carrier. Sofia Connect maintains a direct PoP in Istanbul (Equinix IL2), enabling both IP Transit and DWDM wavelength services with this low-latency profile.",
    faq_q_latency_london: "What is the latency between Sofia and London?",
    faq_a_latency_london:
      "The one-way latency between Sofia and London on Sofia Connect's network is approximately 35 ms. The path connects Sofia PoPs to London TH North via a combination of owned infrastructure and strategic peering agreements. DWDM wavelength services are available on this corridor at 100G and 400G capacities, with protected and unprotected options.",
    faq_q_dark_fiber: "Does Sofia Connect offer Dark Fiber?",
    faq_a_dark_fiber:
      "Yes, in the Sofia metro today. Sofia Connect operates dark fibre that reaches all data centres and commercial and business buildings across the city, on G.652D single-mode fibre. Two commercial models are available: a monthly lease and a 15-year Indefeasible Right of Use (IRU); both include 24/7 NOC monitoring and SLA-backed repair on the Sofia metro fibre. National and cross-border corridors are on our roadmap: Sofia–Kalotina is under construction and in service mid-2027; Route A to Komotini is in design and in service Q1 2028. Onward reach to Frankfurt, Amsterdam and Marseille is partner-delivered from Kalotina once that corridor is live. Contact us for roadmap timing on any planned route.",
    faq_q_dwdm: "What DWDM capacities are available from Sofia Connect?",
    faq_a_dwdm:
      `Sofia Connect's DWDM service offers wavelengths at ${DWDM_CAPACITY_STEPS_LABEL} on coherent optics. DWDM is delivered from all ${DWDM_POP_COUNT} Points of Presence in ${COUNTRIES_SERVED_COUNT} countries, from Dubai, Yerevan and Tbilisi to Istanbul, Athens, Frankfurt and London, and at the Sofia Connect Edge sites across Bulgaria (Plovdiv, Stara Zagora, Burgas, Varna, Balchik, Dobrich, Montana, Pleven, Blagoevgrad). The availability SLA is ${DWDM_SLA_PERCENT} and the typical lead time is ${DWDM_LEAD_EN}. 1+1 protection is available as an option.`,
    faq_q_pops: "How many Points of Presence does Sofia Connect have?",
    faq_a_pops:
      "Sofia Connect operates 18 Points of Presence \u2014 9 international carrier PoPs plus 9 Sofia Connect Edge sites forming our Bulgarian domestic backbone. Internationally: Sofia (Equinix SO1/SO2, Telepoint), Frankfurt (Equinix FR5, New Telco), Istanbul (Equinix IL2), Athens (Lamda Hellix), London (TH North), Tbilisi (Cloud9), Yerevan (Firebird), Agarak (Armenia\u2013Iran terrestrial landing), and Dubai (Datamena DC1). Domestically across Bulgaria: Plovdiv, Stara Zagora, Burgas, Varna, Balchik (Caucasus Cable System submarine landing station to Georgia across the Black Sea), Dobrich, Montana, Pleven, and Blagoevgrad \u2014 all delivering IP Transit and DWDM with 2\u20136 ms latency into the Sofia core. The Balchik landing lets us route Europe\u2194Caucasus traffic without transiting T\u00FCrkiye, which is a key differentiator on the east\u2013west corridor, while Blagoevgrad serves the Struma corridor toward Greece and North Macedonia.",
    faq_q_naas: "What is NaaS and does Sofia Connect support it?",
    faq_a_naas:
      "Network-as-a-Service (NaaS) is a delivery model that allows customers to configure, order, and manage network services on demand \u2014 without manual carrier intervention for each change. Sofia Connect fully supports NaaS through a self-service portal and a RESTful API. Customers can select product type, PoP, bandwidth, and contract term; receive real-time indicative pricing; and submit provisioning requests that trigger automated workflows. BGP session management, bandwidth adjustments, and service lifecycle management are all available via the portal or the API endpoint at api.sofia-connect.net.",
    faq_q_sla: "What SLA does Sofia Connect offer?",
    faq_a_sla:
      `Sofia Connect offers a ${IP_TRANSIT_SLA_PERCENT} availability SLA for IP Transit services and a ${DWDM_SLA_PERCENT} availability SLA for DWDM wavelength services. Both SLAs are backed by financial penalties: customers receive service credits when availability falls below the contracted threshold within any calendar month. The 24/7 NOC provides continuous monitoring. SLA terms are defined per service type in the Master Service Agreement and can be customized for enterprise or carrier-grade contracts.`,
    faq_q_quote: "How can I get a quote from Sofia Connect?",
    faq_a_quote:
      "Quotes can be obtained through three channels. First, via the NaaS self-service portal, which provides real-time indicative pricing for IP Transit, Ethernet/L2VPN, DWDM wavelengths, and Dark Fiber. Second, via the website contact form at sofia-connect.net/contact, where the sales team responds within one business day. Third, by contacting the sales team directly at sales@sofia-connect.net or by phone at +35924200300. For enterprise and carrier agreements requiring custom terms, SLAs, or route engineering, direct contact is recommended.",
    faq_q_asn: "What is Sofia Connect's ASN?",
    faq_a_asn:
      "Sofia Connect's Autonomous System Number is AS47872. It is registered with RIPE NCC, the Regional Internet Registry for Europe, the Middle East, and Central Asia. AS47872 maintains BGP sessions with 96+ peer networks at major Internet Exchange Points including MegaIX (Sofia), DE-CIX (Frankfurt), GR-IX (Athens), and others. The ASN can be verified in the RIPE NCC database at ripe.net.",
    faq_q_ipv6: "Does Sofia Connect support IPv6?",
    faq_a_ipv6:
      "Yes. Sofia Connect supports IPv6 in dual-stack mode across all services. Both IPv4 and IPv6 routing tables are advertised to IP Transit customers via BGP, and all PoP infrastructure is dual-stack enabled. Customers can request IPv6 address space allocation or bring their own Provider Independent (PI) address space and announce it via their BGP session with AS47872. IPv6 support is included at no additional cost for IP Transit customers.",
    faq_aria_list: "Frequently asked questions",

    // Consent banner + preference centre
    consent_banner_title: "Cookies and analytics",
    consent_banner_body:
      "We use non-essential cookies and browser storage only for site analytics (Google Analytics 4, Vercel Analytics and Speed Insights, and one A/B test on our homepage). These are off until you accept. Rejecting will not affect how the site works.",
    consent_banner_accept: "Accept",
    consent_banner_reject: "Reject",
    consent_banner_preferences: "Preferences",
    consent_banner_learn_more: "Privacy policy",
    consent_banner_aria_label: "Cookie consent",

    consent_prefs_title: "Cookie preferences",
    consent_prefs_intro:
      "You can change your choice at any time. Withdrawal takes effect immediately \u2014 non\u2011essential storage set after this point will be blocked and existing analytics cookies we control will be cleared.",
    consent_prefs_essential_title: "Strictly necessary",
    consent_prefs_essential_body:
      "Language preference, theme, quote\u2011form draft you have not yet submitted, and the \u201Cwelcome back\u201D subtitle. These do not identify you and cannot be turned off because the site would not function without them.",
    consent_prefs_essential_state: "Always on",
    consent_prefs_analytics_title: "Analytics",
    consent_prefs_analytics_body:
      "Google Analytics 4, Vercel Analytics and Speed Insights, and the homepage CTA A/B test. Used to measure aggregate traffic and page performance and to compare CTA variants. No advertising, no cross\u2011site tracking, no profiling.",
    consent_prefs_analytics_state_granted: "On",
    consent_prefs_analytics_state_denied: "Off",
    consent_prefs_toggle_aria: "Toggle analytics consent",
    consent_prefs_save: "Save",
    consent_prefs_reject_all: "Reject all",
    consent_prefs_accept_all: "Accept all",
    consent_prefs_close: "Close",
    consent_prefs_status_saved: "Preferences saved.",

    footer_cookie_preferences: "Cookie preferences",

    // Header + Footer navigation labels (PR #116)
    nav_about: "About",
    nav_about_desc: "About Sofia Connect EAD",
    nav_locations: "Locations",
    nav_locations_desc_short: "Every PoP, city by city",
    nav_locations_desc_full: "Every Sofia Connect PoP \u2014 city by city",
    nav_case_studies: "Case Studies",
    nav_case_studies_desc: "Partnership stories",
    nav_news_short: "News",
    nav_news_desc: "Press & announcements",
    nav_knowledge_base_short: "Knowledge Base",
    nav_knowledge_base_desc: "Guides & documentation",
    nav_resources_desc: "Whitepapers & documentation",
    nav_interactive_map_3d: "Interactive Map (3D)",
    nav_interactive_map_3d_desc:
      "Scroll through Frankfurt \u2192 Sofia \u2192 Black Sea \u2192 Caucasus \u2192 Gulf in 3D",
    footer_web_design: "Web Design: Sofia Connect EAD",
    footer_powered_by_vercel: "Powered by Vercel Edge \u2014 Green Hosted",
    footer_vercel_tooltip:
      "This site runs on Vercel's carbon-neutral edge network",

    // SC-NIA transparency notice
    ai_notice_heading: "About this assistant",
    ai_notice_body:
      "This is an AI assistant. Your messages are sent to OpenAI, a third\u2011party model provider based in the United States, for processing. Do not send personal, confidential or sensitive information.",
    ai_notice_privacy_link: "Privacy policy",
    ai_notice_dismiss: "I understand",

    // Form Art. 13 notices
    form_notice_contact:
      "We use the details you enter to answer your enquiry. Sofia Connect EAD is the data controller. See our",
    form_notice_newsletter:
      "By subscribing you agree to receive emails from Sofia Connect EAD. You can unsubscribe at any time. See our",
    form_notice_configurator:
      "We use your name, company, email and phone number to prepare and send you a quote. Sofia Connect EAD is the data controller. See our",
    form_notice_privacy_link: "privacy policy",

    // SC-NIA chat drawer (F4 fix)
    scnia_open_aria: "Open SC-NIA Network Intelligence Agent",
    scnia_close_aria: "Close",
    scnia_send_aria: "Send message",
    scnia_agent_name: "Network Intelligence Agent",
    scnia_context_prefix: "User is currently viewing:",
    scnia_greeting: "Hello!",
    scnia_prompt: "How can I help you?",
    scnia_input_placeholder: "Ask a question\u2026",
    scnia_input_placeholder_limit: "Message limit reached",
    scnia_beta_footer:
      "SC-NIA is in beta. Changes are staged only \u2014 NOC executes.",
    scnia_message_limit: "Message limit reached.",
    scnia_new_conversation: "New conversation",
    scnia_suggestion_services: "What services do you offer?",
    scnia_suggestion_ip_transit: "I need IP Transit in Sofia",
    scnia_suggestion_l2vpn: "How does L2VPN work?",
    scnia_suggestion_10g_quote: "I need a quote for 10Gbps",
    scnia_suggestion_services_desc: "Overview of all products",
    scnia_suggestion_ip_transit_desc: "High-capacity connectivity",
    scnia_suggestion_l2vpn_desc: "Ethernet and MPLS services",
    scnia_suggestion_10g_quote_desc: "Pricing estimates and configurator",

    // SC-NIA full page (F4 fix)
    scnia_page_subtitle:
      "Sofia Connect Network Intelligence Agent \u2014 technical consultation, REST API guidance, monitoring insights and intent-based assistance for {asn}. Security first: SC-NIA stages changes for NOC review.",
    scnia_page_choose_topic: "Choose a topic or type your question",
    scnia_page_quick_links: "Quick Links",
    scnia_page_services_desc: "IP Transit, L2VPN, DWDM, Dark Fiber",
    scnia_page_network_desc: "Peering, IX, BGP information",
    scnia_page_configurator_label: "Configurator",
    scnia_page_configurator_desc: "NaaS platform, online quote",
    scnia_page_contact_desc: "+359 2 420 0300",
    scnia_page_beta_mode: "Beta Mode",
    scnia_page_beta_disclaimer_lead:
      "SC-NIA is in beta. Changes are staged only \u2014 NOC executes. Responses are informational and do not constitute an official quote.",
    scnia_page_beta_disclaimer_middle: "For exact pricing use the",
    scnia_page_beta_disclaimer_or: "or",
    scnia_page_beta_disclaimer_end: ".",
    scnia_page_configurator_link_label: "configurator",
    scnia_page_contact_link_label: "contact us",
    scnia_page_direct_contact: "Direct Contact",
    scnia_page_sales_department: "Sales Department",

    // ── /network/ scrollytelling page ──
    network_page_eyebrow: "Sofia Connect · Network",
    network_page_title: "One backbone, four corridors, three continents.",
    network_page_intro:
      "Scroll to follow the route from Frankfurt through Sofia, across the Black Sea, into the Caucasus and onward toward the Gulf.",
    network_page_cta_configure: "Configure a route",
    network_page_cta_talk_engineering: "Talk to engineering",
    network_page_toggle_show_edge: "Show Bulgaria edge",
    network_page_toggle_hide_edge: "Hide Bulgaria edge",
    // Phase 1 — Gateway
    network_page_p1_eyebrow: "Phase 1 — Gateway",
    network_page_p1_title: "Western Europe to the Balkans",
    network_page_p1_body:
      "From London and Frankfurt's carrier-dense fabrics, Sofia Connect's backbone reaches into South-East Europe over multiple diverse paths — 400G wavelengths, IP transit and dark fibre, fully managed end-to-end.",
    network_page_p1_cta1: "Configure Frankfurt ↔ Sofia",
    network_page_p1_cta2: "Talk to sales",
    // Phase 2 — Hub
    network_page_p2_eyebrow: "Phase 2 — Hub",
    network_page_p2_title: "Sofia, the regional crossroads",
    network_page_p2_body:
      "Sofia is where European, Black Sea and Eastern Mediterranean routes converge. Our core sits at Equinix SO1/SO2 and Telepoint, peered at BIX.BG and BG-IX, and reachable from every major IXP via remote peering.",
    network_page_p2_cta1: "See all Sofia PoPs",
    network_page_p2_cta2: "Peering & IX details",
    // Phase 3 — Bridge
    network_page_p3_eyebrow: "Phase 3 — Bridge",
    network_page_p3_title: "Crossing the Black Sea",
    network_page_p3_body:
      "Two independent paths — terrestrial Sofia → Istanbul and the Balchik–Constanța submarine route — give every customer a redundant bridge between Europe and Türkiye, with sub-15 ms RTT to Istanbul.",
    network_page_p3_cta1: "Configure Sofia ↔ Istanbul",
    network_page_p3_cta2: "Subsea cable case study",
    // Phase 4 — Corridor
    network_page_p4_eyebrow: "Phase 4 — Corridor",
    network_page_p4_title: "The Digital Silk Road",
    network_page_p4_body:
      "From Istanbul we extend through the Caucasus to Tbilisi, Yerevan and the Agarak landing point, linking European hyperscalers to Armenia, the Caspian basin and onward toward Central Asia.",
    network_page_p4_cta1: "Request a Caucasus quote",
    network_page_p4_cta2: "Latency from Sofia",
    // Phase 5 — Onward
    network_page_p5_eyebrow: "Phase 5 — Onward",
    network_page_p5_title: "Into the Gulf",
    network_page_p5_body:
      "From Tbilisi we hand off to long-haul partners reaching Dubai and the wider Gulf — completing one of the shortest commercial paths between Frankfurt and the UAE that avoids Egypt entirely.",
    network_page_p5_cta1: "Frankfurt → Dubai design call",
    network_page_p5_cta2: "Network Advisor",
    // Explore mode
    network_page_explore_eyebrow: "Explore mode",
    network_page_explore_title:
      "Drag to rotate, scroll to zoom — hover any PoP or fibre for details.",
    network_page_explore_body:
      "Use the toggle to overlay the Bulgarian edge network — every metro and regional PoP that hangs off Sofia.",
    network_page_explore_cta1: "Get instant pricing",
    network_page_explore_cta2: "Schedule a design call",
    // Bottom 2D map section
    network_page_map_eyebrow: "Network map",
    network_page_map_title: "In service today, and what is planned",
    network_page_map_aria_label: "Sofia Connect network map",

    // ── Mobile mesh / InteractiveNetworkMap ──
    imap_loading: "Loading network map...",
    mesh_metric_health: "Health",
    mesh_metric_avg_rtt: "Avg RTT",
    mesh_metric_pops_live: "PoPs live",
    mesh_balchik_body_prefix:
      "Caucasus Cable System submarine landing to Georgia across the Black Sea \u2014 ",
    mesh_balchik_body_bypass: "bypasses T\u00fcrkiye",
    mesh_balchik_body_suffix: " terrestrial transit.",
    mesh_balchik_sofia_to_balchik_label: "Sofia \u2192 Balchik:",
    mesh_group_international: "International PoPs",
    mesh_group_sc_edge_bg: "Sofia Connect Edge (Bulgaria)",
    mesh_footer_note: "{count} PoPs \u00b7 {asn} \u00b7 One SLA, one NOC",
    imap_route_tracer: "Route Tracer",
    imap_from_placeholder: "From PoP\u2026",
    imap_to_placeholder: "To PoP\u2026",
    imap_reset: "Reset",
    imap_route_result_label: "Route",
    imap_est_latency_label: "Est. Latency",
    imap_legend_primary: "Primary route",
    imap_legend_caucasus: "Caucasus route",
    imap_legend_submarine: "Submarine cable",
    imap_legend_bg_edge: "BG Edge (Sofia Connect)",
    // ── /solutions/ai-ready-infrastructure/ (AI interconnect page) ──
    ai_page_eyebrow: "AI interconnect",
    ai_page_h1: "Interconnect for AI build-outs",
    ai_page_subhead: "Dedicated optical capacity between AI sites, exchanges and cloud on-ramps across the Middle East, Caucasus and Europe corridor.",
    ai_page_scope: "Sofia Connect does not operate GPU capacity and does not provide colocation. We are the network between the sites.",
    ai_page_cta_talk: "Talk to our team",
    ai_page_cta_network: "View the network",
    ai_page_l1_eyebrow: "Layer 1",
    ai_page_l1_heading: "A wavelength is not a faster internet circuit",
    ai_page_l1_body: "A lambda is dedicated optical capacity. There is no queueing, no contention and no loss from oversubscription, because the capacity is not shared. That comes from the physics, not from configuration. It is the property distributed work between sites depends on, and it is what separates a wavelength from a well-provisioned IP path.",
    ai_page_spec_dedicated: "dedicated optical capacity",
    ai_page_spec_deterministic: "deterministic latency",
    ai_page_spec_coherent: "coherent optics",
    ai_page_spec_protection: "protection, optional",
    ai_page_power_eyebrow: "Power",
    ai_page_power_heading: "AI sites are chosen by power. Then by network.",
    ai_page_power_body: "The corridor connects regions with their own generation to the European interconnection hubs. If the power case points east, the network does not have to be the reason it stays west.",
    ai_page_services_eyebrow: "Services",
    ai_page_services_heading: "What runs between the sites",
    ai_page_svc_dwdm_body: "Point-to-point wavelengths on coherent optics, with 1+1 protection as an option.",
    ai_page_svc_ip_body: "Full-table BGP transit for traffic that has to reach the internet.",
    ai_page_svc_l2_body: "Private Layer 2 circuits between sites and cloud on-ramps, outside the public internet.",
    ai_page_svc_df_body: "Unlit G.652D fibre for campus and data-centre interconnect. Your equipment, your protocols.",
    ai_page_svc_delivery: "Delivery",
    ai_page_svc_lead: "Lead time",
    ai_page_svc_sla: "SLA",
    ai_page_svc_pops: "{n} PoPs",
    ai_page_learn_more: "Service details",
    ai_page_corridor_eyebrow: "Corridor",
    ai_page_corridor_heading: "Where the network runs",
    ai_page_corridor_reach: "DWDM is delivered from all {pops} points of presence, in {countries} countries.",
    ai_page_lat_frankfurt: "Sofia → Frankfurt",
    ai_page_lat_istanbul: "Sofia → Istanbul",
    ai_page_ccs_body: "Our routes to the Caucasus cross the Black Sea on the Caucasus Cable System between Poti and Balchik, without transit through Türkiye.",
    ai_page_ix_heading: "Internet exchanges",
    ai_page_ix_port: "port",
    ai_page_calc_cta: "Size your north-south requirement →",
    ai_page_calc_note: "Indicative model · published assumptions, not a measurement or a capacity commitment. Route-specific design available on request.",
    ai_page_final_heading: "Tell us which sites you need to connect.",
    ai_page_final_body: "We will map the path between them: a DWDM wavelength, IP Transit, or Sofia metro dark fibre.",
    ai_page_final_quote: "Request a quote",
    ai_page_final_services: "Browse all services",
  },

  bg: {
    // Header & Navigation
    services: "Услуги",
    network: "Мрежа",
    tools: "Инструменти",
    get_a_quote: "Поискай оферта",
    naas_platform: "NaaS Платформа",
    more: "Още",

    // Hero
    // SC-4 fix: "Кариер коридорът" → "Операторският коридор". The
    // <title> already used the correct adjective form; this keeps the
    // client-side dictionary in sync with src/lib/hero-strings.ts (SSR copy).
    hero_title_1: "Операторският коридор между",
    hero_title_2: "Близкия изток, Кавказ и Европа",
    hero_subtitle:
      "Двупосочен оптичен гръбнак, DWDM и IP транзит, свързващи Дубай, Ереван, Тбилиси и Истанбул с Франкфурт, София, Атина и Лондон — един AS, един SLA, един NOC.",
    explore_network: "Разгледай мрежата",
    try_naas_configurator: "Пробвай NaaS конфигуратора",

    // Trust metrics
    pops: "Точки на присъствие",
    to_frankfurt: "до Франкфурт",
    uptime_sla: "SLA за достъпност",
    bgp_peers: "BGP партньори",
    points_of_presence: "Точки на присъствие",
    countries_kpi: "Държави",
    spec_capacity: "капацитет",
    spec_ports: "портове",
    spec_sla: "sla",
    spec_lead: "въвеждане",
    spec_fibre: "влакно",
    spec_scope: "обхват",
    spec_scope_sofia_metro: "София (метро)",
    spec_days_short: "дн",

    // Footer section headers
    products: "Продукти",
    resources: "Ресурси",
    legal: "Правна информация",
    all_rights_reserved: "Всички права запазени.",

    // Common
    contact: "Контакт",
    contact_us: "Свържете се с нас",
    learn_more: "Научете повече",

    // WhySection
    why_section_eyebrow: "Разликата",
    why_section_heading: "Защо Sofia Connect",

    // WhySection differentiator titles
    diff_strategic_location: "Стратегическо местоположение",
    diff_ai_ready: "Капацитет за AI",
    diff_naas: "NaaS Самообслужване",
    diff_noc: "NOC 24/7",

    // BentoServices
    bento_eyebrow: "Продукти за свързаност",
    bento_heading: "Създадени за мащаб",
    bento_subtitle:
      "От тъмно влакно до напълно управлявани облачни връзки — всичко, от което мрежата ви се нуждае, налично при поискване.",

    // BentoServices - service descriptions
    ip_transit_desc:
      `Премиум глобален рутинг чрез Tier-1 пиъринг на множество PoPs. От ${IP_TRANSIT_MIN_LABEL} до ${IP_TRANSIT_MAX_LABEL}, гъвкаво таксуване.`,
    l2vpn_desc:
      "Прозрачна Layer 2 свързаност. Точка до точка и мултиточка през MPLS.",
    dwdm_desc:
      `Специализиран оптичен капацитет, ${DWDM_CAPACITY_RANGE_LABEL} на вълна. Кохерентна оптика, детерминирана латентност.`,
    dark_fiber_desc:
      "Неосветено G.652D влакно в София (метро) днес; национални и трансгранични коридори — в пътната карта. Ваше оборудване, ваши протоколи.",

    // NaaS Configurator
    naas_eyebrow: "Архитект на решения",
    naas_heading: "Проектирайте Вашата Свързаност",
    naas_subtitle:
      "Получете ориентировъчна цена в реално време и изпратете запитване за оферта директно — без ръчно попълване на формуляри.",

    // GlobalLiveMesh
    mesh_eyebrow: "Мрежова инфраструктура в реално време",
    mesh_heading: "Глобална жива мрежа",
    mesh_desc: "Интерактивен 3D изглед на нашата инфраструктура с трафик в реално време. Щракнете върху маршрут за Jitter и Packet Loss. Щракнете върху възел за детайли.",
    mesh_view_all_pops: "Виж всички 18 PoP →",

    // TrustedBy
    trusted_eyebrow: "Доверие по света",
    trusted_heading: "Мрежова екосистема",
    trusted_subtitle:
      "Партньори, доставчици и клиенти в Европа, Кавказ и Близкия Изток, свързани с Sofia Connect.",

    // Testimonials
    testimonials_eyebrow: "Партньорски референции",
    testimonials_heading: "Какво казват нашите партньори-оператори",
    testimonials_subtitle:
      "Проверени референции от международни оператори, които обслужваме по коридора между Близкия изток, Кавказ и Европа.",

    read_faq: "Прочети ЧЗВ",

    // FAQ
    faq_eyebrow: "База знания за оператори",
    faq_heading: "Често задавани въпроси",
    faq_subtitle:
      "Технически и комерсиални отговори за мрежови инженери, екипи за доставки и оператори — обхващащи услуги, SLA и начало на работа.",
    still_have_questions: "Имате още въпроси?",
    faq_team_responds:
      "Нашият екип за връзки с оператори отговаря в рамките на един работен ден.",
    contact_sales: "Свържете се с нас",

    // CTA Banner
    cta_heading: "Готови ли сте за свързване?",
    cta_subtitle:
      "Независимо дали имате нужда от оферта, консултация или поддръжка — нашите мрежови инженери са на разположение денонощно.",
    try_naas: "Опитай NaaS Конфигуратора",

    // WhySection differentiator descriptions
    diff_strategic_desc:
      "На кръстопътя на Европа, Балканите и Турция — маршрути, за които повечето оператори се нуждаят от 3+ прехода.",
    diff_ai_desc:
      `Специализирани вълни до ${DWDM_CAPACITY_MAX_LABEL} с детерминирана латентност между AI площадки, точки за обмен и облачни входове.`,
    diff_naas_desc:
      "Конфигурирайте, поръчайте и управлявайте свързаност при поискване чрез нашия портал или API. Цени в реално време, мигновено предоставяне.",
    diff_noc_desc:
      "Денонощни мрежови операции с финансови SLA санкции, а не маркетингови обещания.",

    // WhySection fact box
    why_fact_text:
      "Sofia Connect оперира 18 PoPs — 9 международни (София, Франкфурт, Истанбул, Лондон, Тбилиси, Атина, Дубай, Ереван, Агарак) и 9 Sofia Connect Edge съоръжения в България, включително Balchik Caucasus Cable System landing станция, която байпасва терестриалния транзит през Турция.",
    why_fact_as: "Автономна система: AS47872 · Регистрирана в RIPE NCC",
    experience_diff: "Усетете разликата",

    // Footer link labels
    footer_peering: "Пиъринг & IX (AS47872)",
    footer_network_status: "Статус на мрежата (демо)",
    footer_looking_glass: "Looking Glass (демо)",
    footer_naas_configurator: "NaaS Конфигуратор",
    footer_developer_portal: "Портал за разработчици",
    footer_client_portal: "Клиентски портал",
    footer_latency_checker: "Проверка на латентност",
    footer_roi_calculator: "ROI Калкулатор",
    footer_network_advisor: "Мрежови съветник",
    footer_ai_advisor: "SC-NIA",
    footer_provisioning_demo: "Демо на провизиониране",
    footer_knowledge_base: "База знания",
    footer_news: "Новини",
    footer_insights: "Анализи",
    footer_faq: "ЧЗВ",
    footer_terms: "Общи условия",
    footer_privacy: "Политика за поверителност",
    footer_aup: "Политика за допустима употреба",
    footer_sla: "SLA Резюме",

    // Personalized subtitle
    welcome_back: "Добре дошли отново — продължете от там, където спряхте",

    // Dropdown descriptions (Header)
    desc_ip_transit: "Глобален IP рутинг с висок капацитет",
    desc_l2vpn: "Частна Layer 2 свързаност",
    desc_dwdm: `${DWDM_CAPACITY_RANGE_LABEL} оптичен транспорт`,
    desc_dark_fiber: "Тъмно влакно за пълен контрол",
    desc_ai_ready: "Оптимизирана за GPU мрежа с ниска латентност",
    desc_solution_finder: "Водена анкета за намиране на услуга",
    desc_peering: "Точки за обмен & сървъри за маршрути",
    desc_network_status: "Демонстрация на страница за статус",
    desc_looking_glass: "Демонстрация на BGP & traceroute",
    desc_network_mesh: "3D изглед на топология в реално време",
    desc_developer_portal: "API документация & интеграции",
    desc_latency_checker: "Тест за латентност с едно щракване",
    desc_roi_calculator: "Оценка на спестявания & ROI",
    desc_network_advisor: "Персонализиран план за свързаност",
    desc_ai_advisor: "Network Intelligence Agent — технически консултации, API, мониторинг",
    desc_provisioning: "Интерактивен процес на поръчка",
    desc_bandwidth_calculator: "Север-юг капацитет на МВ IT натоварване",

    // —— /tools/bandwidth-calculator/ (bg) ——
    bw_nav_label: "Калкулатор на капацитет",
    bw_page_title: "Калкулатор на капацитет",
    bw_page_description:
      "Оразмерява север-юг WAN капацитет за AI датацентър в зависимост от IT мощност в МВ, големина на модела и политика на checkpoint. Индикативен модел — не е измерване и не е ангажимент за капацитет.",
    bw_h1: "Калкулатор на WAN капацитет за AI датацентър",
    bw_eyebrow: "СЕВЕР-ЮГ САЙЗИНГ",
    bw_intro:
      "Север-юг трафикът на AI сайт е функция на инсталираната IT мощност, големината на най-големия модел и честотата на дренаж на checkpoint. Всичко по-долу е нормализирано на мегават. Моделирани са пет независими потоци; средното и пикът се доминират от различни потоци.",
    bw_indicative_line:
      "Индикативен модел · публикувани допускания, не измерване и не ангажимент за капацитет. Дизайн, специфичен за маршрут, — при поискване.",

    bw_primer_heading: "Три неща, които си заслужава да знаете, преди да движите плъзгачите",
    bw_primer_1_title: "Мегавати, не GPU-та.",
    bw_primer_1_body:
      "Трафикът не зависи от броя GPU-та, а от мегаватите. Поколенията карти се сменят на всеки година и половина и броят им на ват се променя; трафикът на ват — почти не. Размерване по GPU остарява бързо.",
    bw_primer_2_title: "Пик и средно се движат от различни неща.",
    bw_primer_2_body:
      "Средното е inference. Пикът е почти изцяло източване на checkpoint-и. Ако ги смесиш, грешиш в двете посоки наведнъж.",
    bw_primer_3_title: "Коефициент Φ.",
    bw_primer_3_body:
      "Независимите наематели не правят checkpoint едновременно, така че пиковете не се сумират. При трима наематели Φ ≈ 0.70; при осем — Φ ≈ 0.40. Практическият извод: договорното разместване на прозорците за checkpoint е най-евтиният начин да си купите капацитет.",

    bw_step_inputs: "Стъпка 1 — Входни данни",
    bw_step_results: "Стъпка 2 — Резултати",

    bw_arch_label: "Архетип на сайта",
    bw_arch_cap: "Каптивна обучителна среда",
    bw_arch_mix: "Смесен neocloud",
    bw_arch_inf: "Предимно inference",
    bw_arch_cap_desc:
      "Единичен наемател, основно обучение. Високо checkpoint задържане (R = 14), ниска inference интензивност.",
    bw_arch_mix_desc:
      "Множество наематели, обучение и сървиране едновременно. R = 14, inference интензивност 1.0.",
    bw_arch_inf_desc:
      "Предимно serving. R = 2 (само тегла), inference интензивност 4.0. Пикът се доминира от inference брстове.",

    bw_mw_label: "Инсталирана IT мощност",
    bw_mw_unit: "МВ",
    bw_mw_hint: "Обща IT мощност на сайта, не входящата ел. мощност.",
    bw_P_label: "Най-голем модел на сайта",
    bw_P_unit: "T параметъра",
    bw_P_hint:
      "Трилиони параметри. Определя обема на checkpoint: байтове на параметър × коефициент на задържане.",
    bw_drain_label: "Прозорец на checkpoint дренаж",
    bw_drain_unit: "с",
    bw_drain_hint:
      "Секунди за дренаж на един checkpoint извън сайта. По-къс дренаж — по-висок пик; по-дълъг дренаж — усреднява се.",
    bw_interval_label: "Интервал между checkpoint",
    bw_interval_unit: "мин",
    bw_interval_hint:
      "Минути между checkpoint събития на бръстер. Определя duty cycle на средното.",
    bw_bursters_label: "Независими бръстери",
    bw_bursters_hint:
      "Паралелни обучителни процеси. Независими значи, че дренажните им прозорци не са корелирани — това позволява Φ да слезе под 1.",
    bw_bursters_readout: "{count} (фи {phi})",

    bw_card_avg_title: "Средно",
    bw_card_avg_sub: "{value} Gb/s на МВ",
    bw_card_peak_title: "Пик след фи",
    bw_card_peak_sub: "{value} Gb/s на МВ",
    bw_card_link_title: "Необходима връзка при 65% утилизация",
    bw_card_link_sub: "Пик, разделен на целевата утилизация.",
    bw_card_lambdas_title: "Лямбди по два диверсифицирани маршрута",
    bw_card_lambdas_sub: "Закръглено нагоре до четен брой, по половината на всеки маршрут.",
    bw_card_lambdas_value: "{n} ×400G",

    bw_breakdown_title: "Разбивка на пика",
    bw_breakdown_sub: "Дял от пиковия Tb/s по поток.",
    bw_flow_checkpoint: "Checkpoint дренаж",
    bw_flow_dataset: "Зареждане на данни",
    bw_flow_inference: "Inference I/O",
    bw_flow_distill: "Дистилация · copy-out",
    bw_flow_ops: "Операции · control plane",
    bw_flow_row_aria: "{flow}: {value} на пик, {share}% от общото.",

    bw_copy_button: "Копирай обобщение",
    bw_copy_done: "Копирано",
    bw_share_button: "Копирай споделяем линк",
    bw_share_copied: "Линкът е копиран",
    bw_reset_button: "Нулирай",

    bw_notes_heading: "Бележки към методиката",
    bw_note_mw_title: "Защо мегавати, а не GPU",
    bw_note_mw_body:
      "GPU поколенията променят броя на ват отколо 18-месечен цикъл. Hopper, Blackwell и каквото следва променят FLOPs и памет на ват, но север-юг трафикът на ват почти не се мени — checkpoint, зареждането на данни и inference скалират с мощността до compute-а, а не със SKU на акселератора. Сайзинг по брой GPU остарява в момента, в който ново поколение влезе на сайта.",
    bw_note_phi_title: "Защо съществува Φ",
    bw_note_phi_body:
      "Прозорците на дренаж на независими наематели не са корелирани, така че пиковете им не се сумират — сайтът вижда свивка, а не сума. Φ е ефективен коефициент на корелация за N бръстера: по дефолт 1.00 при N=1, 0.75 при N=2 и пада с 0.05 за всеки допълнителен бръстер до под 0.4. Договорното разместване на дренажните прозорци в SLA на наемателите е най-евтиният капацитетен ъпгрейд: с три или повече бръстера кара Φ към 0.4, т.е. повече от наполовява пиковата checkpoint широчина без да се пипа физическата инфраструктура.",
    bw_note_limits_title: "Граници на модела",
    bw_note_limits_body:
      "Моделът предполага независими бръстери. Споделен cluster scheduler ги корелира и Φ се връща на 1. Междусайтово разпределено обучение не е включено — това е отделен термин от 10–100 Tb/s за паралелизъм между датацентрове и принадлежи на разговор на ниво маршрут, а не на per-site сайзинг. Inference коефициентите са калибрирани за текст плюс лек RAG; генерация на медия (изображения и особено видео) счупва скалата с един ред на величина и трябва да се оразмерява директно спрямо измерен egress от serving стека.",

    bw_cta_heading: "Ние продаваме лямбдите, които този калкулатор брои.",
    bw_cta_body:
      "За дизайн, специфичен за маршрут — диверсифицирани пътища, cross-connect, латентност и условия за доставка — отворете конфигуратора или пишете на мрежовия екип. Двата пътя приемат един и същ бриф.",
    bw_cta_primary: "Запитване за дизайн на маршрут",
    bw_cta_secondary: "Отвори конфигуратора",
    bw_cta_note:
      "Конфигураторът не се попълва от тази страница — резултатът на модела е индикативен, а конфигураторът квотира реален капацитет. Пренасяйте числото съзнателно.",

    bw_copy_summary_title: "Sofia Connect — модел за капацитет на AI датацентър (индикативно)",
    bw_copy_summary_methodology:
      "Индикативен модел на базата на публикувани допускания — не е измерване от Sofia Connect и не е ангажимент за капацитет. Дизайн, специфичен за маршрут, — при поискване.",

    // Contact page
    contact_eyebrow: "Свържете се",
    contact_hero_title: "Свържете се с нас",
    contact_hero_subtitle:
      "Свържете се директно с нужния ви екип — независимо дали проучвате нови решения за свързаност или имате нужда от незабавна техническа помощ.",
    contact_channel_sales: "Продажби и оферти",
    contact_channel_noc: "NOC 24/7",
    contact_channel_phone: "Телефон",
    contact_channel_office: "Офис",
    contact_channel_live: "Наживо",
    contact_office_details: "Данни за офиса",
    contact_office_legal_name: "Официално наименование",
    contact_office_asn: "ASN",
    contact_office_hours: "Работно време",
    contact_office_hours_value: "Пон–Пет, 09:00\u201318:00 EET",
    contact_office_noc_availability: "Достъпност на NOC",

    // Contact form
    contact_form_title: "Изпратете съобщение",
    contact_form_subtitle: "Попълнете формуляра и ще се свържем с вас в рамките на един работен ден.",
    contact_form_name: "Име и фамилия",
    contact_form_name_placeholder: "Иван Иванов",
    contact_form_company: "Компания",
    contact_form_company_placeholder: "Acme Telecom",
    contact_form_email: "Имейл",
    contact_form_email_placeholder: "ivan@acmetelecom.com",
    contact_form_phone: "Телефон (по желание)",
    contact_form_phone_placeholder: "+359 2 123 4567",
    contact_form_department: "Отдел",
    contact_form_department_placeholder: "Изберете отдел",
    contact_form_service: "Интерес към услуга",
    contact_form_service_placeholder: "Изберете услуга (по желание)",
    contact_form_message: "Съобщение",
    contact_form_message_placeholder: "Опишете вашите изисквания за свързаност, обеми, срокове...",
    contact_form_submit: "Изпрати съобщение",
    contact_form_submitting: "Изпращане...",
    contact_form_success_title: "Съобщението е изпратено",
    contact_form_success_text:
      "Благодарим ви, че се свържахте със Sofia Connect. Екипът ни ще отговори в рамките на един работен ден. За спешни NOC проблеми позвънете директно.",
    contact_form_send_another: "Изпрати още едно съобщение",
    contact_form_error_title: "Грешка при изпращане",
    contact_form_error_text_prefix: "Нещо не се получи. Моля, опитайте отново или се свържете директно на",
    contact_form_error_text_or: "или",
    contact_form_try_again: "Опитай отново",

    // Status badge
    status_all_operational: "Всички системи работят нормално",
    status_degraded: "Намалена производителност",
    status_outage: "Активен инцидент",
    status_loading: "Проверка на статуса\u2026",
    status_unknown: "Статус",
    status_short_operational: "Работи",
    status_short_degraded: "С ограничения",
    status_short_outage: "Инцидент",
    status_short_loading: "\u2026",
    status_short_unknown: "Статус",
    status_aria_prefix: "Статус на мрежата",

    // /status/ demonstration page (bg)
    status_demo_eyebrow: "Демонстрация — не са данни от реалната мрежа",
    status_demo_h1: "Статус на мрежата — демонстрация",
    status_demo_intro:
      "Тази страница показва как би изглеждало таблото за статус на мрежата на Sofia Connect за нашите {count} точки на присъствие. Всички стойности на тази страница са или договорни SLA цели, или са генерирани в тестова среда — те не отразяват реалната производителност на мрежата.",
    status_demo_banner_title:
      "Това е демонстрационна страница. Показаните стойности са симулирани.",
    status_demo_banner_body_1:
      "Латентността, джитерът, загубите на пакети и натоварването се генерират в тестова среда, и на живия сайт също. Те не отразяват реалната работа на мрежата. Стойностите за наличност са договорни SLA цели, а не измервания.",
    status_demo_banner_body_2:
      "За реален статус на мрежата и информация за инциденти се свържете с нашия NOC на",
    status_demo_banner_body_or: "или",
    status_demo_banner_aria: "Известие за демонстрация",
    status_demo_pill_operational: "Демо състояние — Работи",
    status_demo_pill_degraded: "Демо състояние — Влошено",
    status_demo_pill_outage: "Демо състояние — Инцидент",
    status_demo_pill_aria:
      "Демонстрационно състояние, изчислено от симулирани стойности",
    status_demo_sla_headline_eyebrow: "IP Transit — договорна SLA цел",
    status_demo_sla_headline_note:
      "Договорен месечен ангажимент за наличност на IP Transit. Не е измерена стойност.",
    status_demo_routes_h2: "Симулирано състояние на маршрутите",
    status_demo_routes_subnote:
      "Стойностите по-долу се генерират в тестова среда. Те не отразяват реалната производителност на опорната мрежа.",
    status_demo_routes_refresh_note:
      "Симулирани данни — обновяват се на всеки 30 секунди",
    status_demo_services_h2: "Договорни SLA цели по услуги",
    status_demo_services_subnote:
      "Стойностите за наличност по-долу са договорни SLA ангажименти, прочетени от src/lib/network-metrics.ts. Те не са измервания.",
    status_demo_services_footnote:
      "Времената за отговор и отстраняване са договорни цели за реакция, а не наблюдавани стойности.",
    status_demo_col_service: "Услуга",
    status_demo_col_sla_target: "SLA цел за наличност",
    status_demo_col_uptime_target: "SLA цел за наличност",
    status_demo_col_max_downtime: "Макс. престой",
    status_demo_col_response: "Отговор",
    status_demo_col_repair: "Отстраняване",
    status_demo_sla_h2: "SLA ангажименти",
    status_demo_sla_subnote:
      "Договорни ангажименти по клас услуга. Не са измерване на реалната производителност.",
    status_demo_sla_footnote:
      "SLA-то за тъмни влакна покрива само тъмните влакна в експлоатация в софийския метро сегмент. Планираните трасета до Калотина и до гръцката граница се изключват до датите им на въвеждане в експлоатация.",
    status_demo_latency_h2: "Референтни стойности за латентност",
    status_demo_latency_subnote:
      "Референтни стойности от нашия продукционен DWDM коридор. Договорни/спецификационни стойности, не са измервания в реално време.",
    status_demo_noc_h2: "Свържете се с NOC за реален статус на мрежата",
    status_demo_noc_body:
      "NOC на Sofia Connect е на разположение 24/7 за реален статус на мрежата, докладване на инциденти и събития, засягащи услугата.",
    status_demo_next_refresh: "Следващо симулирано обновяване след {secs}с",

    // /looking-glass/ demonstration page (bg)
    lg_demo_eyebrow: "Демонстрация — не е инструмент за реална диагностика",
    lg_demo_h1: "Looking Glass — демонстрация",
    lg_demo_intro:
      "Тази страница показва как би изглеждала мрежова диагностична повърхност на Sofia Connect. Формата по-долу не изпълнява BGP заявки, traceroute или ping към нашата инфраструктура — тя връща предварително подготвен отговор. За реална диагностика от нашата мрежа се свържете с NOC.",
    lg_demo_banner_title:
      "Това е демонстрационна страница. Нито една заявка от тук не достига до нашата мрежа.",
    lg_demo_banner_body_1:
      "BGP заявките, traceroute и ping на тази страница връщат предварително подготвен отговор след кратко изчакване. Те не запитват реални рутери, реални маршрутни таблици или реални дестинации от която и да е точка на присъствие на Sofia Connect.",
    lg_demo_banner_body_2:
      "За реални BGP заявки, traceroute и диагностика от нашата мрежа се свържете с нашия NOC на",
    lg_demo_banner_body_or: "или",
    lg_demo_banner_aria: "Известие за демонстрация",
    lg_demo_result_notice:
      "Демонстрационен резултат — не е изпълнена заявка към мрежата на Sofia Connect. За реална диагностика се свържете с NOC.",
    lg_demo_footer_note:
      "За публични looking-glass услуги вижте RIPE RIS, RouteViews или PeeringDB.",

    // NaaS configurator step labels
    naas_step_product: "Продукт",
    naas_step_route: "Маршрут",
    naas_step_capacity: "Капацитет",
    naas_step_terms: "Условия",
    naas_step_review: "Преглед и изпращане",

    // Resources page
    resources_eyebrow: "База знания",
    resources_hero_title: "Ресурси и анализи",
    resources_hero_subtitle:
      "Казуси, техническа документация и обновления на мрежата от Sofia Connect — оператор за операторска свързаност в Югоизточна Европа.",
    resources_category_all: "Всички",
    resources_category_case_studies: "Казуси",
    resources_category_documentation: "Документация",
    resources_category_network_updates: "Обновления на мрежата",
    resources_show_less: "Покажи по-малко",
    resources_read_more: "Прочети още",
    resources_view_document: "Виж документа",
    resources_no_resources: "Все още няма ресурси в тази категория.",

    // FAQ Q&A
    faq_q_ip_transit: "Какво е IP Transit и как Sofia Connect го доставя?",
    faq_a_ip_transit:
      "IP Transit е платена услуга, която предоставя достъп до глобалната BGP таблица за маршрутизация през upstream мрежата на оператора. Sofia Connect доставя IP Transit под AS47872, с пиъринг с 96+ мрежи на основните европейски и регионални Internet Exchange точки. Клиентите се свързват в София (Equinix SO1/SO2, Telepoint) или Франкфурт (Equinix FR5, New Telco) и получават пълна BGP таблица с резервирани upstream пътища, NOC мониторинг 24/7 и 99.99% SLA, подкрепен от финансови санкции. Скоростите на портовете са от 1G до 400G.",
    faq_q_latency_frankfurt: "Каква е латентността между София и Франкфурт?",
    faq_a_latency_frankfurt:
      "Еднопосочната латентност между София и Франкфурт в мрежата на Sofia Connect е около 24.5 ms. Това се постига през директен оптичен маршрут с малък брой преходи между PoP-овете в София (Equinix SO1/SO2) и Франкфурт (Equinix FR5, New Telco), без излишен транзит през трети страни. Маршрутът поддържа DWDM вълни до 400G с кохерентна оптика за детерминистична латентност.",
    faq_q_latency_istanbul: "Каква е латентността между София и Истанбул?",
    faq_a_latency_istanbul:
      "Еднопосочната латентност между София и Истанбул в мрежата на Sofia Connect е около 12 ms. Географската близост на София до Истанбул прави това един от най-конкурентните маршрути ЮИ Европа–Турция при един оператор. Sofia Connect поддържа директен PoP в Истанбул (Equinix IL2), което позволява както IP Transit, така и DWDM вълнови услуги с този нисък профил на латентност.",
    faq_q_latency_london: "Каква е латентността между София и Лондон?",
    faq_a_latency_london:
      "Еднопосочната латентност между София и Лондон в мрежата на Sofia Connect е около 35 ms. Маршрутът свързва Софийските PoP-ове с Лондон TH North чрез комбинация от собствена инфраструктура и стратегически пиъринг споразумения. DWDM вълнови услуги са налични на този коридор при 100G и 400G капацитети, със защитени и незащитени варианти.",
    faq_q_dark_fiber: "Предлага ли Sofia Connect тъмно влакно?",
    faq_a_dark_fiber:
      "Да, в София (метро) днес. Sofia Connect оперира тъмно влакно, което достига до всички дата центрове и бизнес сгради в града, на G.652D single-mode влакно. Налични са два търговски модела: месечен наем и 15-годишен Indefeasible Right of Use (IRU); и двата включват 24/7 NOC мониторинг и SLA за време за възстановяване на влакното в София. Националните и трансгранични коридори са в нашата пътна карта: София–Калотина е в строителство и в експлоатация средата на 2027; Маршрут A до Комотини е в проектиране и в експлоатация през К1 2028. Достъпът напред към Франкфурт, Амстердам и Марсилия е партньорска доставка от Калотина, след пуска на коридора. Свържете се с нас за срокове по планираните маршрути.",
    faq_q_dwdm: "Какви DWDM капацитети са налични от Sofia Connect?",
    faq_a_dwdm:
      `DWDM услугата на Sofia Connect предлага вълни с капацитет ${DWDM_CAPACITY_STEPS_LABEL} с кохерентна оптика. DWDM се доставя от всички ${DWDM_POP_COUNT} точки на присъствие в ${COUNTRIES_SERVED_COUNT} държави, от Дубай, Ереван и Тбилиси до Истанбул, Атина, Франкфурт и Лондон, както и в площадките Sofia Connect Edge в България (Пловдив, Стара Загора, Бургас, Варна, Балчик, Добрич, Монтана, Плевен, Благоевград). SLA за достъпност е ${DWDM_SLA_PERCENT}, а обичайният срок за доставка е ${DWDM_LEAD_BG}. Защита 1+1 се предлага по избор.`,
    faq_q_pops: "Колко точки на присъствие има Sofia Connect?",
    faq_a_pops:
      "Sofia Connect оперира 18 точки на присъствие — 9 международни операторски PoP-ове плюс 9 Sofia Connect Edge площадки, образуващи националния български гръбнак. На международно ниво: София (Equinix SO1/SO2, Telepoint), Франкфурт (Equinix FR5, New Telco), Истанбул (Equinix IL2), Атина (Lamda Hellix), Лондон (TH North), Тбилиси (Cloud9), Ереван (Firebird), Агарак (терестриално лъчене Армения–Иран) и Дубай (Datamena DC1). В България: Пловдив, Стара Загора, Бургас, Варна, Балчик (подводна ландинг станция на Caucasus Cable System до Грузия през Черно море), Добрич, Монтана, Плевен и Благоевград — всичките доставят IP Transit и DWDM с латентност 2\u20136 ms до Софийското ядро. Лъченето в Балчик позволява трафик Европа\u2194Кавказ без транзит през Т\u00FCrkiye, което е ключов диференциатор на изток–западния коридор, докато Благоевград обслужва коридора Струма към Гърция и Северна Македония.",
    faq_q_naas: "Какво е NaaS и поддържа ли го Sofia Connect?",
    faq_a_naas:
      "Network-as-a-Service (NaaS) е модел на доставяне, който позволява на клиентите да конфигурират, поръчват и управляват мрежови услуги при поискване — без ръчна намеса на оператора за всяка промяна. Sofia Connect напълно поддържа NaaS чрез портал за самообслужване и RESTful API. Клиентите могат да изберат тип на продукта, PoP, бандуидтх и срок; да получат индикативни цени в реално време и да подават заявки за провизиониране, които покрепват автоматизирани работни процеси. Управление на BGP сесии, настройка на капацитета и жизненият цикъл на услугите са достъпни през портала или API endpoint на api.sofia-connect.net.",
    faq_q_sla: "Какъв SLA предлага Sofia Connect?",
    faq_a_sla:
      `Sofia Connect предлага ${IP_TRANSIT_SLA_PERCENT} SLA за достъпност на IP Transit услуги и ${DWDM_SLA_PERCENT} SLA за DWDM вълнови услуги. И двата SLA са подкрепени с финансови санкции: клиентите получават сервизни кредити, когато достъпността падне под договорения праг в рамките на календарен месец. NOC 24/7 осигурява постоянен мониторинг. Условията на SLA се дефинират за всеки тип услуга в Master Service Agreement и могат да бъдат персонализирани за корпоративни или операторски договори.`,
    faq_q_quote: "Как мога да получа оферта от Sofia Connect?",
    faq_a_quote:
      "Оферти могат да се получат по три канала. Първият — чрез NaaS портала за самообслужване, който предоставя индикативни цени в реално време за IP Transit, Ethernet/L2VPN, DWDM вълни и Dark Fiber. Вторият — чрез формуляра за контакт на sofia-connect.net/contact, при който екипът по продажби отговаря в рамките на един работен ден. Третият — чрез директен контакт с екипа на sales@sofia-connect.net или телефон +35924200300. За корпоративни и операторски договори, изискващи индивидуални условия, SLA или рутинг инженеринг, препоръчваме директен контакт.",
    faq_q_asn: "Какъв е ASN на Sofia Connect?",
    faq_a_asn:
      "Номерът на Автономната система на Sofia Connect е AS47872. Регистриран е в RIPE NCC — регионалният интернет регистър за Европа, Близкия изток и Централна Азия. AS47872 поддържа BGP сесии с 96+ съседни мрежи на основните Internet Exchange точки, включително MegaIX (София), DE-CIX (Франкфурт), GR-IX (Атина) и други. ASN може да се провери в базата данни на RIPE NCC на ripe.net.",
    faq_q_ipv6: "Поддържа ли Sofia Connect IPv6?",
    faq_a_ipv6:
      "Да. Sofia Connect поддържа IPv6 в dual-stack режим във всички услуги. И IPv4, и IPv6 таблици за маршрутизация се рекламират на IP Transit клиенти през BGP, а цялата PoP инфраструктура е dual-stack съвместима. Клиентите могат да поискат разпределено IPv6 адресно пространство или да обявхват собствено Provider Independent (PI) пространство чрез BGP сесията си с AS47872. Поддръжката на IPv6 е включена без допълнителна такса за IP Transit клиенти.",
    faq_aria_list: "Често задавани въпроси",

    // Consent banner + preference centre
    consent_banner_title: "Бисквитки и анализи",
    consent_banner_body:
      "Използваме несъществено съхранение в браузъра само за анализ на сайта (Google Analytics 4, Vercel Analytics и Speed Insights, и един A/B тест на началната страница). Те са изключени, докато не приемете. Отказът не влияе върху работата на сайта.",
    consent_banner_accept: "Приемам",
    consent_banner_reject: "Отказвам",
    consent_banner_preferences: "Настройки",
    consent_banner_learn_more: "Политика за поверителност",
    consent_banner_aria_label: "Съгласие за бисквитки",

    consent_prefs_title: "Настройки за бисквитки",
    consent_prefs_intro:
      "Можете да промените избора си по всяко време. Оттеглянето влиза в сила веднага \u2014 несъщественото съхранение след този момент ще бъде блокирано и съществуващите аналитични бисквитки, които контролираме, ще бъдат изчистени.",
    consent_prefs_essential_title: "Строго необходими",
    consent_prefs_essential_body:
      "Езикова настройка, тема, чернова на формуляра за оферта, която все още не сте изпратили, и субтитърът „добре дошли отново“. Не Ви идентифицират и не могат да бъдат изключени, защото сайтът не би работил без тях.",
    consent_prefs_essential_state: "Винаги активни",
    consent_prefs_analytics_title: "Анализи",
    consent_prefs_analytics_body:
      "Google Analytics 4, Vercel Analytics и Speed Insights, и A/B тестът на CTA на началната страница. Използват се за измерване на обобщения трафик, производителността и сравняване на CTA варианти. Без реклама, без междусайтово проследяване, без профилиране.",
    consent_prefs_analytics_state_granted: "Активни",
    consent_prefs_analytics_state_denied: "Изключени",
    consent_prefs_toggle_aria: "Превключване на съгласие за анализи",
    consent_prefs_save: "Запази",
    consent_prefs_reject_all: "Отказвам всички",
    consent_prefs_accept_all: "Приемам всички",
    consent_prefs_close: "Затвори",
    consent_prefs_status_saved: "Настройките са запазени.",

    footer_cookie_preferences: "Настройки за бисквитки",

    // Header + Footer navigation labels (PR #116)
    nav_about: "За нас",
    nav_about_desc: "За Sofia Connect EAD",
    nav_locations: "Локации",
    nav_locations_desc_short: "Всеки PoP, град по град",
    nav_locations_desc_full: "Всеки PoP на Sofia Connect \u2014 град по град",
    nav_case_studies: "Клиентски истории",
    nav_case_studies_desc: "Истории от партньорства",
    nav_news_short: "Новини",
    nav_news_desc: "Прессъобщения и обяви",
    nav_knowledge_base_short: "База знания",
    nav_knowledge_base_desc: "Ръководства и документация",
    nav_resources_desc: "Технически материали и документация",
    nav_interactive_map_3d: "Интерактивна карта (3D)",
    nav_interactive_map_3d_desc:
      "Разгледайте маршрута Франкфурт \u2192 София \u2192 Черно море \u2192 Кавказ \u2192 Персийския залив в 3D",
    footer_web_design: "Web дизайн: Sofia Connect EAD",
    footer_powered_by_vercel: "Работи с Vercel Edge \u2014 зелен хостинг",
    footer_vercel_tooltip:
      "Този сайт работи на carbon\u2011neutral edge мрежата на Vercel",

    // SC-NIA transparency notice
    ai_notice_heading: "За този асистент",
    ai_notice_body:
      "Това е AI асистент. Съобщенията Ви се изпращат за обработка към OpenAI \u2014 доставчик на модел от трета страна, установен в САЩ. Не изпращайте лични, поверителни или чувствителни данни.",
    ai_notice_privacy_link: "Политика за поверителност",
    ai_notice_dismiss: "Разбирам",

    // Form Art. 13 notices
    form_notice_contact:
      "Използваме въведените от Вас данни, за да отговорим на запитването Ви. Sofia Connect EAD е администратор на лични данни. Вижте нашата",
    form_notice_newsletter:
      "С абонирането се съгласявате да получавате имейли от Sofia Connect EAD. Можете да се отпишете по всяко време. Вижте нашата",
    form_notice_configurator:
      "Използваме Вашето име, фирма, имейл и телефон, за да подготвим и Ви изпратим оферта. Sofia Connect EAD е администратор на лични данни. Вижте нашата",
    form_notice_privacy_link: "политика за поверителност",

    // SC-NIA chat drawer (F4 fix)
    scnia_open_aria: "Отвори SC-NIA — Агент за мрежова интелигентност",
    scnia_close_aria: "Затвори",
    scnia_send_aria: "Изпрати съобщение",
    scnia_agent_name: "Агент за мрежова интелигентност",
    scnia_context_prefix: "Потребителят в момента разглежда:",
    scnia_greeting: "Здравейте!",
    scnia_prompt: "С какво мога да Ви помогна?",
    scnia_input_placeholder: "Задайте въпрос\u2026",
    scnia_input_placeholder_limit: "Достигнат лимит на съобщенията",
    scnia_beta_footer:
      "SC-NIA е в бета. Промените са само планирани — NOC ги изпълнява.",
    scnia_message_limit: "Достигнат лимит на съобщенията.",
    scnia_new_conversation: "Нов разговор",
    scnia_suggestion_services: "Какви услуги предлагате?",
    scnia_suggestion_ip_transit: "Трябва ми IP Transit в София",
    scnia_suggestion_l2vpn: "Как работи L2VPN?",
    scnia_suggestion_10g_quote: "Трябва ми оферта за 10Gbps",
    scnia_suggestion_services_desc: "Преглед на всички продукти",
    scnia_suggestion_ip_transit_desc: "Висококапацитетна свързаност",
    scnia_suggestion_l2vpn_desc: "Ethernet и MPLS услуги",
    scnia_suggestion_10g_quote_desc: "Ценови оценки и конфигуратор",

    // SC-NIA full page (F4 fix)
    scnia_page_subtitle:
      "Агентът за мрежова интелигентност на Sofia Connect — технически консултации, насоки към REST API, анализи от мониторинг и intent-based помощ за {asn}. Сигурността — на първо място: SC-NIA планира промените за преглед от NOC.",
    scnia_page_choose_topic: "Изберете тема или напишете въпроса си",
    scnia_page_quick_links: "Бързи връзки",
    scnia_page_services_desc: "IP Transit, L2VPN, DWDM, Dark Fiber",
    scnia_page_network_desc: "Peering, IX, BGP информация",
    scnia_page_configurator_label: "Конфигуратор",
    scnia_page_configurator_desc: "NaaS платформа, онлайн оферта",
    scnia_page_contact_desc: "+359 2 420 0300",
    scnia_page_beta_mode: "Бета режим",
    scnia_page_beta_disclaimer_lead:
      "SC-NIA е в бета. Промените са само планирани — NOC ги изпълнява. Отговорите са информативни и не представляват официална оферта.",
    scnia_page_beta_disclaimer_middle: "За точни цени използвайте",
    scnia_page_beta_disclaimer_or: "или",
    scnia_page_beta_disclaimer_end: ".",
    scnia_page_configurator_link_label: "конфигуратора",
    scnia_page_contact_link_label: "се свържете с нас",
    scnia_page_direct_contact: "Директен контакт",
    scnia_page_sales_department: "Отдел Продажби",

    // ── /network/ scrollytelling page ──
    network_page_eyebrow: "Sofia Connect · Мрежа",
    network_page_title: "Един гръбнак, четири коридора, три континента.",
    network_page_intro:
      "Проследете маршрута от Франкфурт през София, през Черно море, към Кавказ и нататък към Персийския залив.",
    network_page_cta_configure: "Конфигурирай маршрут",
    network_page_cta_talk_engineering: "Свържете се с екипа",
    network_page_toggle_show_edge: "Покажи български edge",
    network_page_toggle_hide_edge: "Скрий български edge",
    // Phase 1 — Gateway
    network_page_p1_eyebrow: "Фаза 1 — Вход",
    network_page_p1_title: "От Западна Европа към Балканите",
    network_page_p1_body:
      "От carrier-гъстите възли в Лондон и Франкфурт гръбнакът на Sofia Connect влиза в Югоизточна Европа по няколко независими маршрута — 400G дължини на вълната, IP транзит и dark fibre, които управляваме от край до край.",
    network_page_p1_cta1: "Конфигурирай Франкфурт ↔ София",
    network_page_p1_cta2: "Свържи се с Продажби",
    // Phase 2 — Hub
    network_page_p2_eyebrow: "Фаза 2 — Център",
    network_page_p2_title: "София, регионалното кръстовище",
    network_page_p2_body:
      "В София се пресичат европейските, черноморските и източносредиземноморските маршрути. Ядрото е в Equinix SO1/SO2 и Telepoint, с peering в BIX.BG и BG-IX, и е достъпно от всеки голям IXP през remote peering.",
    network_page_p2_cta1: "Всички PoP в София",
    network_page_p2_cta2: "Детайли за peering и IX",
    // Phase 3 — Bridge
    network_page_p3_eyebrow: "Фаза 3 — Мост",
    network_page_p3_title: "През Черно море",
    network_page_p3_body:
      "Два независими маршрута — терестриалният София → Истанбул и подводният Балчик–Констанца — дават на всеки клиент резервирана връзка между Европа и Тюркия, с RTT под 15 ms до Истанбул.",
    network_page_p3_cta1: "Конфигурирай София ↔ Истанбул",
    network_page_p3_cta2: "Case study: подводен кабел",
    // Phase 4 — Corridor
    network_page_p4_eyebrow: "Фаза 4 — Коридор",
    network_page_p4_title: "Цифровият Път на коприната",
    network_page_p4_body:
      "От Истанбул продължаваме през Кавказ до Тбилиси, Ереван и landing точката в Агарак, свързвайки европейските hyperscale оператори с Армения, Каспийския басейн и нататък към Централна Азия.",
    network_page_p4_cta1: "Оферта за Кавказ",
    network_page_p4_cta2: "Latency от София",
    // Phase 5 — Onward
    network_page_p5_eyebrow: "Фаза 5 — Напред",
    network_page_p5_title: "Към Персийския залив",
    network_page_p5_body:
      "От Тбилиси предаваме към long-haul партньори, които достигат Дубай и останалия Залив, с което затваряме един от най-късите търговски маршрути между Франкфурт и ОАЕ, без да минава през Египет.",
    network_page_p5_cta1: "Design call Франкфурт → Дубай",
    network_page_p5_cta2: "Network Advisor",
    // Explore mode
    network_page_explore_eyebrow: "Режим „Изследвай“",
    network_page_explore_title:
      "Завъртете с влачене, приближете със скрол — задръжте курсора върху PoP или влакно за детайли.",
    network_page_explore_body:
      "Използвайте превключвателя, за да наложите българската edge мрежа — всеки метро и регионален PoP, закачен за София.",
    network_page_explore_cta1: "Получи цена веднага",
    network_page_explore_cta2: "Запази design call",
    // Bottom 2D map section
    network_page_map_eyebrow: "Карта на мрежата",
    network_page_map_title: "В експлоатация днес и какво предстои",
    network_page_map_aria_label: "Карта на мрежата на Sofia Connect",

    // ── Mobile mesh / InteractiveNetworkMap ──
    imap_loading: "Зареждане на картата…",
    mesh_metric_health: "Състояние",
    mesh_metric_avg_rtt: "Ср. RTT",
    mesh_metric_pops_live: "PoP активни",
    mesh_balchik_body_prefix:
      "Подводна ландинг точка на Caucasus Cable System към Грузия през Черно море — ",
    mesh_balchik_body_bypass: "заобикаля Тюркия",
    mesh_balchik_body_suffix: " по суша.",
    mesh_balchik_sofia_to_balchik_label: "София → Балчик:",
    mesh_group_international: "Международни PoP",
    mesh_group_sc_edge_bg: "Sofia Connect Edge (България)",
    mesh_footer_note: "{count} PoP \u00b7 {asn} \u00b7 Един SLA, един NOC",
    imap_route_tracer: "Трасиране на маршрут",
    imap_from_placeholder: "От PoP\u2026",
    imap_to_placeholder: "До PoP\u2026",
    imap_reset: "Изчисти",
    imap_route_result_label: "Маршрут",
    imap_est_latency_label: "Очакван latency",
    imap_legend_primary: "Основен маршрут",
    imap_legend_caucasus: "Кавказки маршрут",
    imap_legend_submarine: "Подводен кабел",
    imap_legend_bg_edge: "BG Edge (Sofia Connect)",
    // ── /solutions/ai-ready-infrastructure/ (AI interconnect page) ──
    ai_page_eyebrow: "Свързаност за AI",
    ai_page_h1: "Свързаност между площадки за AI",
    ai_page_subhead: "Специализиран оптичен капацитет между AI площадки, точки за обмен и облачни входове по коридора Близкия изток, Кавказ и Европа.",
    ai_page_scope: "Sofia Connect не оперира GPU капацитет и не предлага колокация. Ние сме мрежата между площадките.",
    ai_page_cta_talk: "Свържете се с екипа",
    ai_page_cta_network: "Вижте мрежата",
    ai_page_l1_eyebrow: "Ниво 1",
    ai_page_l1_heading: "Вълната не е по‑бърз интернет канал",
    ai_page_l1_body: "Лямбдата е специализиран оптичен капацитет. Няма опашки, няма конкуренция за ресурс и няма загуби от преангажиране, защото капацитетът не се споделя. Това идва от физиката, не от конфигурацията. От това свойство зависи разпределената работа между площадки и то отличава вълната от добре оразмерен IP маршрут.",
    ai_page_spec_dedicated: "специализиран оптичен капацитет",
    ai_page_spec_deterministic: "детерминирана латентност",
    ai_page_spec_coherent: "кохерентна оптика",
    ai_page_spec_protection: "защита, по избор",
    ai_page_power_eyebrow: "Мощност",
    ai_page_power_heading: "AI площадките се избират по мощност. После по мрежа.",
    ai_page_power_body: "Коридорът свързва региони със собствено производство на електроенергия с европейските точки за взаимно свързване. Ако сметката за мощност сочи на изток, мрежата не бива да е причината проектът да остане на запад.",
    ai_page_services_eyebrow: "Услуги",
    ai_page_services_heading: "Какво минава между площадките",
    ai_page_svc_dwdm_body: "Вълни от точка до точка върху кохерентна оптика, със защита 1+1 по избор.",
    ai_page_svc_ip_body: "BGP транзит с пълна таблица за трафика, който трябва да стигне до интернет.",
    ai_page_svc_l2_body: "Частни канали на ниво 2 между площадки и облачни входове, извън публичния интернет.",
    ai_page_svc_df_body: "Неосветено влакно G.652D за свързване на кампуси и центрове за данни. Ваше оборудване, ваши протоколи.",
    ai_page_svc_delivery: "Доставка",
    ai_page_svc_lead: "Срок",
    ai_page_svc_sla: "SLA",
    ai_page_svc_pops: "{n} точки",
    ai_page_learn_more: "Подробно за услугата",
    ai_page_corridor_eyebrow: "Коридор",
    ai_page_corridor_heading: "Къде минава мрежата",
    ai_page_corridor_reach: "DWDM се доставя от всички {pops} точки на присъствие в {countries} държави.",
    ai_page_lat_frankfurt: "София → Франкфурт",
    ai_page_lat_istanbul: "София → Истанбул",
    ai_page_ccs_body: "Маршрутите ни към Кавказ пресичат Черно море по Caucasus Cable System между Поти и Балчик, без транзит през Турция.",
    ai_page_ix_heading: "Точки за обмен на трафик",
    ai_page_ix_port: "порт",
    ai_page_calc_cta: "Оразмерете капацитета си север-юг →",
    ai_page_calc_note: "Индикативен модел · публикувани допускания, не измерване и не ангажимент за капацитет. Проектиране за конкретен маршрут при поискване.",
    ai_page_final_heading: "Кажете ни кои площадки трябва да свържете.",
    ai_page_final_body: "Ще предложим пътя между тях: DWDM вълна, IP Transit или тъмно влакно в София.",
    ai_page_final_quote: "Поискайте оферта",
    ai_page_final_services: "Всички услуги",
  },
};

// ─── Context ───────────────────────────────────────────────────────────────────

type I18nContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof Translations) => string;
};

const I18nContext = createContext<I18nContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key) => translations.en[key],
});

// ─── Provider ──────────────────────────────────────────────────────────────────

export const LANG_COOKIE = "sc-lang";
const STORAGE_KEY = "sc-lang";

function readClientInitialLanguage(serverLanguage?: Language): Language {
  // SSR: server has already determined the language from cookie/path — use it.
  if (typeof window === "undefined") return serverLanguage ?? "en";

  // Client hydrate: prefer the server-determined language to avoid mismatch.
  if (serverLanguage === "bg" || serverLanguage === "en") return serverLanguage;

  // Fallback (no server hint): legacy localStorage value.
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored === "bg" || stored === "en") return stored;
  } catch {
    // localStorage unavailable (privacy mode) — keep default
  }
  return "en";
}

function writeLanguageCookie(lang: Language) {
  if (typeof document === "undefined") return;
  // 1-year cookie, root path, lax SameSite — readable by middleware and SSR.
  const oneYear = 60 * 60 * 24 * 365;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${LANG_COOKIE}=${lang}; Max-Age=${oneYear}; Path=/; SameSite=Lax${secure}`;
}

export function LanguageProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  initialLanguage?: Language;
}) {
  const [language, setLanguageState] = useState<Language>(() =>
    readClientInitialLanguage(initialLanguage)
  );

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    writeLanguageCookie(lang);
  }, []);

  const t = useCallback(
    (key: keyof Translations): string => translations[language][key],
    [language]
  );

  return createElement(
    I18nContext.Provider,
    { value: { language, setLanguage, t } },
    children
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useTranslation() {
  return useContext(I18nContext);
}

// ─── LanguageSwitcher component ────────────────────────────────────────────────

/**
 * Build the path for the opposite locale, preserving the current pathname.
 * Examples:
 *   switchLocalePath("/services", "bg")     => "/bg/services"
 *   switchLocalePath("/bg/services", "en")  => "/services"
 *   switchLocalePath("/bg", "en")           => "/"
 *   switchLocalePath("/", "bg")             => "/bg"
 */
function switchLocalePath(pathname: string, target: Language): string {
  // Strip any leading /bg or /bg/ prefix to get the canonical path
  let canonical = pathname;
  if (canonical === "/bg") canonical = "/";
  else if (canonical.startsWith("/bg/")) canonical = canonical.slice(3);

  if (target === "en") return canonical;
  return canonical === "/" ? "/bg" : `/bg${canonical}`;
}

export function LanguageSwitcher() {
  const { language, setLanguage } = useTranslation();

  const handleSwitch = (target: Language) => {
    if (target === language) return;
    setLanguage(target);
    if (typeof window !== "undefined") {
      const next = switchLocalePath(window.location.pathname, target) +
        window.location.search + window.location.hash;
      // Use full navigation (not router.push) to ensure middleware runs
      // and the new SSR output reflects the locale immediately.
      window.location.assign(next);
    }
  };

  return createElement(
    "div",
    {
      className: "flex items-center",
      style: {
        border: "1px solid var(--card-border)",
        borderRadius: "6px",
        overflow: "hidden",
        height: "36px",
      },
    },
    // EN button. Visible label "EN" doubles up as the accessible name;
    // a `.sr-only` span gives screen readers the full word. No aria-label
    // is set, so axe-core's label-content-name-mismatch rule stays happy
    // (visible text is contained in the accessible name).
    createElement(
      "button",
      {
        onClick: () => handleSwitch("en"),
        className: "px-2.5 h-full text-[11px] font-mono tracking-wider transition-all duration-150",
        // --accent-text: #00d4aa in dark (unchanged), #006b52 in light.
        // var(--accent) #00a882 on --accent-muted was 2.54:1 in light.
        style: {
          background:
            language === "en" ? "var(--accent-muted)" : "transparent",
          color:
            language === "en" ? "var(--accent-text)" : "var(--text-muted)",
          borderRight: "1px solid var(--card-border)",
        },
        "aria-pressed": language === "en",
      },
      "EN",
      createElement(
        "span",
        { className: "sr-only" },
        " \u2014 switch to English"
      )
    ),
    // BG button (mirror of EN)
    createElement(
      "button",
      {
        onClick: () => handleSwitch("bg"),
        className: "px-2.5 h-full text-[11px] font-mono tracking-wider transition-all duration-150",
        style: {
          background:
            language === "bg" ? "var(--accent-muted)" : "transparent",
          color:
            language === "bg" ? "var(--accent-text)" : "var(--text-muted)",
        },
        "aria-pressed": language === "bg",
      },
      "BG",
      createElement(
        "span",
        { className: "sr-only" },
        " \u2014 switch to Bulgarian"
      )
    )
  );
}
